import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef,
} from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  CircularProgress,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Menu,
  ListItemIcon,
  Stack,
  Popover,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip as MuiTooltip,
} from "@mui/material";
import {
  Refresh as RefreshIcon,
  Videocam as VideocamIcon,
  MoreVert as MoreVertIcon,
  CheckCircle as CheckCircleIcon,
  AccessTime as AccessTimeIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Close as CloseIcon,
  Image as ImageIcon,
  PlayArrow as PlayArrowIcon,
  InfoOutlined as InfoOutlinedIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
} from "@mui/icons-material";
import {
  SearchIcon,
  ViewListIcon,
  ViewModuleIcon,
  FilterListIcon,
} from "../../constants/icons";
import { useAlertData } from "../../contexts/AlertDataContext";
import { useAlertStream } from "../../hooks/useAlertStream";
import {
  mapAlertResponseToDashboardRow,
  buildAlertsStreamSearchParams,
  isAlertHistoryUsecaseId,
  extractAlertsHistoryPayload,
  mergeStreamAlertWithBaseline,
} from "../../utils/alertStreamUtils";
import { buildAlertHistoryCarouselSlides } from "../../utils/alertApiPayloadMedia";
import {
  AlertHistoryMediaImage,
  AlertHistoryMediaVideo,
} from "../../components/alertServicesSections/AlertHistoryAuthenticatedMedia";
import { CustomDateRangeDialog } from "../../components/alertServicesSections/CustomDateRangeDialog";
import { ResetFiltersButton } from "../../common/ResetFiltersButton.tsx";
import { AlertServicesPaginationFooter } from "../../components/alertServicesSections/AlertServicesPaginationFooter";
import AlertNotifiedByChips, {
  notifyStringsFromDashboardRuleChannels,
} from "../../components/alertServicesSections/AlertNotifiedByChips";
import {
  dashboardAPI,
  alertsHistoryAPI,
  useCasesAPI,
  camerasAPI,
  zonesAPI,
} from "../../services/api";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Cell,
  LabelList,
  Tooltip,
  Customized,
} from "recharts";

/** Consolidated severity filter: always offer all standard levels (not only levels present on the current page). */
const AI_ALERTS_SEVERITY_FILTER_LEVELS = [
  "Critical",
  "High",
  "Medium",
  "Low",
];

const STATUS_STYLES = {
  Active: { bg: "#FFF3E0", color: "#E65100" },
  Acknowledged: { bg: "#1B5E20", color: "#FFFFFF" },
  Closed: { bg: "#1565C0", color: "#FFFFFF" },
};

/** `value` sent to API as lowercase; `label` is title case for the filter UI. */
const NOTIFICATION_STATUS_FILTER_OPTIONS = [
  // { value: "pending", label: "Pending" },
  { value: "queued", label: "Queued" },
  { value: "sent", label: "Sent" },
  { value: "partial", label: "Partial" },
  { value: "failed", label: "Failed" },
];

/** Same pattern as Alert History: local overrides until refresh/stream replaces row. */
function getEffectiveDashboardRowStatus(row, localStatusOverrides) {

  if (!row || typeof row !== "object") return "Active";
  const k = row.rowKey;
  if (
    k != null &&
    localStatusOverrides &&
    localStatusOverrides[k] !== undefined
  ) {
    return localStatusOverrides[k];
  }
  return row.status ?? "Active";
}

function dashboardRowNotifyChipChannels(row) {
  if (!row) return [];
  const fromApi = row.notifyChannels;
  if (Array.isArray(fromApi) && fromApi.length > 0) {
    return fromApi.filter((x) => typeof x === "string" && String(x).trim());
  }
  if (!Array.isArray(row.channels)) return [];
  return notifyStringsFromDashboardRuleChannels(row.channels);
}

/** Grid hero: prefer still image URL; else video; else empty (placeholder). Matches Alert History grid. */
function dashboardGridHeroMode(row) {
  const videoHref =
    row?.videoUrl && String(row.videoUrl).trim()
      ? String(row.videoUrl).trim()
      : "";
  const photoHref =
    (row?.photoUrl && String(row.photoUrl).trim()) ||
    (row?.thumb && String(row.thumb).trim()) ||
    "";
  if (photoHref) return { kind: "image", videoHref, photoHref };
  if (videoHref) return { kind: "video", videoHref, photoHref };
  return { kind: "none", videoHref, photoHref };
}

/** Matches list table area and grid scroll area so both AI Alerts views share the same height. */
const AI_ALERTS_CONTENT_MIN_HEIGHT = "385px";

/**
 * AI Alerts toolbar date presets — same keys / API mapping as Alert History
 * (`GET /alerts/history`: `period=all` for all dates; `start_date`/`end_date` for custom).
 */
const AI_ALERTS_DATE_PRESETS = [
  { value: "all", label: "All dates" },
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "last7days", label: "Last 7 days" },
  { value: "last30days", label: "Last 30 days" },
  { value: "thisweek", label: "This week" },
  { value: "thismonth", label: "This month" },
  { value: "custom", label: "Custom range" },
];

function aiAlertsDatePresetLabel(value) {
  const row = AI_ALERTS_DATE_PRESETS.find((o) => o.value === value);
  return row ? row.label : "Date";
}

function formatLocalYmd(d) {
  if (!d || !(d instanceof Date) || Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Merge History-compatible date filters into `params` for alertsHistoryAPI.getHistory. */
function appendAiAlertsHistoryDateParams(params, dateRange, customStart, customEnd) {
  const periodMap = {
    today: "today",
    yesterday: "yesterday",
    last7days: "last_7_days",
    last30days: "last_30_days",
    thisweek: "this_week",
    thismonth: "this_month",
  };
  if (dateRange === "all") {
    params.period = "all";
    return;
  }
  if (dateRange === "custom") {
    if (customStart && customEnd) {
      params.start_date = `${formatLocalYmd(customStart)}T00:00:00`;
      params.end_date = `${formatLocalYmd(customEnd)}T23:59:59`;
    }
    return;
  }
  const p = periodMap[dateRange];
  if (p) params.period = p;
}

/** Today: GET /alerts/history baseline page size (stream then merges on top by id). */
const TODAY_ALERTS_BASELINE_PER_PAGE = 50;

function criticalBadgeCount(total) {
  if (!total || total < 1) return 0;
  return Math.max(1, Math.round(total * 0.1));
}

/** Default shape when statistics API fails or returns no KPI block. */
function emptyDashboardStatisticsPayload() {
  return {
    kpi_cards: {
      ai_alert: { count: 0, percentage_change: 0, critical: 0 },
      iot_alert: { count: 0, percentage_change: 0, critical: 0 },
      system_alert: { count: 0, percentage_change: 0, critical: 0 },
    },
    trend_chart: {
      period: null,
      alert_category: null,
      data_points: [],
    },
  };
}

/**
 * Unwrap GET /alerts/dashboard/statistics body: `{ success, message, data }` or raw `data`.
 * @param {unknown} json
 * @returns {Record<string, unknown> | null}
 */
function unwrapDashboardStatisticsResponse(json) {
  if (!json || typeof json !== "object") return null;
  const o = /** @type {Record<string, unknown>} */ (json);
  if (o.data != null && typeof o.data === "object") {
    return /** @type {Record<string, unknown>} */ (o.data);
  }
  return o;
}

const DASHBOARD_ALERTS_SEARCH_SCOPE_TOOLTIP =
  "Search matches: ID, Alert Name, Source, Zone, Notified To, and Notified By. Minimum 2 characters.";

/** Client-side AI Alerts global search — only operator-facing fields (no raw message / timestamps / UUIDs). */
function dashboardRowMatchesSearchQuery(row, qLower, localStatusOverrides) {
  if (!qLower || qLower.length < 2) return true;
  const effStatus = getEffectiveDashboardRowStatus(row, localStatusOverrides);
  const parts = [
    row?.srId,
    row?.alertName,
    row?.alertNameFull,
    row?.severity,
    row?.alertType,
    row?.sourceName,
    row?.zone,
    effStatus,
    row?.notifiedTo,
  ];
  if (Array.isArray(row?.notifyChannels)) {
    for (const ch of row.notifyChannels) {
      if (ch != null) parts.push(ch);
    }
  }
  if (Array.isArray(row?.channels)) {
    for (const ch of row.channels) {
      if (ch?.label != null) parts.push(ch.label);
    }
  }
  const hay = parts
    .map((v) => String(v ?? "").toLowerCase())
    .join(" ");
  return hay.includes(qLower);
}

/** GET /alerts/history & stream: `alert_type` is use-case UUID when possible; else legacy slug. */
function alertTypeParamForHistoryStream(filterValue) {
  const raw = String(filterValue ?? "").trim();
  if (!raw) return "";
  if (isAlertHistoryUsecaseId(raw)) return raw;
  return raw.toLowerCase().replace(/\s+/g, "_");
}

/** Same semantics as Alert History: comma-separated use-case ids / legacy slugs for `alert_type`. */
function alertTypesParamForHistoryStream(selectedValues) {
  if (!Array.isArray(selectedValues) || selectedValues.length === 0) return "";
  const parts = selectedValues
    .map((v) => alertTypeParamForHistoryStream(v))
    .filter(Boolean);
  return parts.join(",");
}

/** Map workspace status labels to GET /alerts/history & stream `status` values. */
function dashboardWorkspaceStatusToApiStatus(ui) {
  const m = {
    Active: "active",
    Acknowledged: "acknowledged",
    Closed: "closed",
  };
  const s = String(ui ?? "").trim();
  if (!s) return "";
  return m[s] ?? s.toLowerCase();
}

/** Normalize GET `/cameras` response to a flat array. */
function extractCamerasArrayFromApi(body) {
  if (Array.isArray(body)) return body;
  if (body && typeof body === "object") {
    const arr = body.data ?? body.results ?? body.Data;
    if (Array.isArray(arr)) return arr;
  }
  return [];
}

/** Normalize GET `/zones` response to a flat array. */
function extractZonesArrayFromApi(body) {
  if (Array.isArray(body)) return body;
  if (body && typeof body === "object") {
    const arr = body.data ?? body.results ?? body.Data;
    if (Array.isArray(arr)) return arr;
  }
  return [];
}

function formatDateTimePill(d) {
  if (!d || Number.isNaN(d.getTime())) return "—";
  const t = d.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
  const day = d.getDate();
  const mon = d.toLocaleString("en-US", { month: "short" }).toLowerCase();
  const y = d.getFullYear();
  return `${t} ${day}${mon}${y}`;
}

function formatOverlayTimestamp(d) {
  if (!d || Number.isNaN(d.getTime())) return "—";
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const h = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  const s = String(d.getSeconds()).padStart(2, "0");
  return `${y}-${mo}-${day} ${h}:${mi}:${s}`;
}

function formatDetailInfoTimestamp(d) {
  if (!d || Number.isNaN(d.getTime())) return "—";
  const day = d.getDate();
  const mon = d.toLocaleString("en-GB", { month: "short" });
  const y = String(d.getFullYear()).slice(-2);
  const t = d.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
  return `${day}${mon}${y} • ${t}`;
}

const DETAIL_SEVERITY_BADGE = {
  Critical: { bg: "#FCE4EC", color: "#C62828" },
  High: { bg: "#FFF3E0", color: "#E65100" },
  Medium: { bg: "#E3F2FD", color: "#1565C0" },
  Low: { bg: "#ECEFF1", color: "#546E7A" },
};

/** Same rule as Alert History: crop thumbnail chip only for ANPR & Face Recognition. */
function dashboardAlertTypeUsesDetectionCropThumbnail(row) {
  if (!row || typeof row !== "object") return false;
  const combined = `${row.alertType ?? ""} ${row.alertNameFull ?? ""} ${row.alertName ?? ""}`
    .toLowerCase()
    .replace(/_/g, " ");
  if (!combined.trim()) return false;
  const isAnpr =
    /\banpr\b/.test(combined) ||
    combined.includes("automatic number plate") ||
    combined.includes("number plate recognition");
  const isFaceRecognition =
    combined.includes("face recognition") ||
    combined.includes("face recognization") ||
    combined.includes("facial recognition");
  return Boolean(isAnpr || isFaceRecognition);
}

/**
 * Alert detail when user clicks a row/card in the AI Alerts table — same media carousel as Alert History.
 */
function AlertSourceDetailDialog({
  open,
  row,
  onClose,
  localStatusOverrides = {},
  onAcknowledge,
  onMarkClosed,
}) {
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [imageLightboxHref, setImageLightboxHref] = useState(null);
  const [detailThumbLoaded, setDetailThumbLoaded] = useState(false);
  const [statusPatchBusy, setStatusPatchBusy] = useState(false);
  const mediaAreaRef = useRef(null);

  const alertDetailMedia = useMemo(() => {
    if (!row) {
      return {
        slides: [],
        thumbUrl: "",
        thumbnailSlideIndex: -1,
        showDetailsThumbnail: false,
      };
    }
    const slides = buildAlertHistoryCarouselSlides(row);
    const thumbUrl = String(row.thumbnailCropUrl || "").trim();
    const thumbnailSlideIndex = thumbUrl
      ? slides.findIndex(
          (s) => s.kind === "image" && String(s.url || "").trim() === thumbUrl,
        )
      : -1;
    const showDetailsThumbnail =
      Boolean(thumbUrl) && dashboardAlertTypeUsesDetectionCropThumbnail(row);
    return {
      slides,
      thumbUrl,
      thumbnailSlideIndex,
      showDetailsThumbnail,
    };
  }, [row]);

  useEffect(() => {
    setDetailThumbLoaded(false);
  }, [open, row?.rowKey, row?.thumbnailCropUrl]);

  useEffect(() => {
    if (open) setCarouselIndex(0);
  }, [open, row?.rowKey]);

  useEffect(() => {
    if (!open) setImageLightboxHref(null);
  }, [open]);

  useEffect(() => {
    if (!imageLightboxHref) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setImageLightboxHref(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [imageLightboxHref]);

  useEffect(() => {
    if (!open) setStatusPatchBusy(false);
  }, [open, row?.rowKey]);

  useEffect(() => {
    if (!open) return undefined;
    const n = alertDetailMedia.slides.length;
    if (n < 2) return undefined;
    const onKey = (e) => {
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        setCarouselIndex((i) => (i - 1 + n) % n);
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        setCarouselIndex((i) => (i + 1) % n);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, alertDetailMedia.slides]);

  if (!row) return null;

  const effectiveRowStatus = getEffectiveDashboardRowStatus(
    row,
    localStatusOverrides,
  );

  const isAcknowledged = effectiveRowStatus === "Acknowledged";
  const isClosed = effectiveRowStatus === "Closed";
  const ackButtonLabel = isAcknowledged ? "Acknowledged" : "Acknowledge";
  const closeButtonLabel = isClosed ? "Closed" : "Close";
  const acknowledgeDisabled =
    statusPatchBusy || isAcknowledged || isClosed || !onAcknowledge;
  const closeWorkspaceDisabled =
    statusPatchBusy || isClosed || !onMarkClosed;

  const sev = DETAIL_SEVERITY_BADGE[row.severity] || {
    bg: "#F5F5F5",
    color: "#212121",
  };
  const titleName = row.alertNameFull || row.alertName || "Alert";

  const { slides, thumbUrl, thumbnailSlideIndex, showDetailsThumbnail } =
    alertDetailMedia;
  const thumbUiVisible = showDetailsThumbnail && detailThumbLoaded;
  const n = slides.length;
  const carouselIdx = n === 0 ? 0 : ((carouselIndex % n) + n) % n;
  const currentSlide = slides[carouselIdx];

  const openImageLightbox = (url) => {
    const href = String(url || "").trim();
    if (href) setImageLightboxHref(href);
  };

  const renderSlide = (s, keySuffix = "") => {
    if (!s?.url) return null;
    if (s.kind === "video") {
      return (
        <AlertHistoryMediaVideo
          key={`${s.url}-${keySuffix}`}
          href={s.url}
          videoSx={{
            width: "100%",
            maxHeight: { xs: 360, sm: 480 },
            objectFit: "contain",
          }}
        />
      );
    }
    return (
      <AlertHistoryMediaImage
        key={`${s.url}-${keySuffix}`}
        href={s.url}
        alt=""
        onClick={() => openImageLightbox(s.url)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            openImageLightbox(s.url);
          }
        }}
        sx={{ width: "100%", display: "block", maxWidth: 820 }}
        imgSx={{
          display: "block",
          width: "100%",
          maxHeight: { xs: 360, sm: 480 },
          objectFit: "contain",
          cursor: "zoom-in",
          transition: "opacity 0.15s ease",
          "&:hover": { opacity: 0.92 },
        }}
      />
    );
  };

  return (
    <>
      <Dialog
        open={open}
        disableEscapeKeyDown
        onClose={(event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            return;
          }
        }}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            width: { xs: "100vw", sm: "90vw" },
            maxWidth: { xs: "100vw", sm: 920 },
            borderRadius: "12px",
            overflow: "hidden",
            boxShadow: "0px 8px 32px rgba(0,0,0,0.18)",
          },
        }}
      >
        <DialogTitle
          sx={{
            margin: 0,
            padding: "16px 24px",
            fontFamily: "Roboto, sans-serif",
            fontSize: "20px",
            color: "rgb(255, 133, 85)",
            fontWeight: 600,
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 1,
            pr: 1,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              flexWrap="wrap"
            >
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  px: 1.25,
                  py: 0.35,
                  borderRadius: "6px",
                  backgroundColor: sev.bg,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "Roboto, sans-serif",
                    fontWeight: 700,
                    fontSize: "12px",
                    color: sev.color,
                    textTransform: "capitalize",
                  }}
                >
                  {row.severity}
                </Typography>
              </Box>
            </Stack>
            <Typography
              sx={{
                fontFamily: "Roboto, sans-serif",
                fontWeight: 700,
                fontSize: "18px",
                color: "#212121",
                mt: 0.75,
              }}
            >
              {titleName}
            </Typography>
            <Typography
              sx={{
                fontFamily: "Roboto, sans-serif",
                fontSize: "14px",
                color: "#4A5565",
                mt: 0.25,
              }}
            >
              {row.sourceName} · {row.zone}
            </Typography>
          </Box>
          <IconButton aria-label="Close" onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          <Box
            ref={mediaAreaRef}
            sx={{
              bgcolor: "#000",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 200,
              maxHeight: { xs: 360, sm: 480 },
              flexShrink: 0,
              overflow: "hidden",
              position: "relative",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                zIndex: 1,
                fontFamily: "Roboto, monospace",
                fontSize: "11px",
                color: "#e5e7eb",
                bgcolor: "rgba(0,0,0,0.45)",
                px: 1,
                py: 0.25,
                borderRadius: 1,
              }}
            >
              {formatOverlayTimestamp(row.at)}
            </Box>
            {n === 0 ? (
              <ImageIcon
                sx={{ fontSize: 56, color: "rgba(255,255,255,0.25)" }}
                aria-hidden
              />
            ) : n === 1 ? (
              renderSlide(slides[0], "single")
            ) : (
              <Box
                sx={{
                  position: "relative",
                  width: "100%",
                  minHeight: { xs: 280, sm: 400 },
                  maxHeight: { xs: 360, sm: 480 },
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  px: { xs: 5, sm: 6 },
                  py: 1,
                }}
              >
                <IconButton
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setCarouselIndex((i) => (i - 1 + n) % n);
                  }}
                  aria-label="Previous slide"
                  sx={{
                    position: "absolute",
                    left: 4,
                    top: "50%",
                    transform: "translateY(-50%)",
                    zIndex: 2,
                    color: "#fff",
                    bgcolor: "rgba(255,255,255,0.12)",
                    "&:hover": { bgcolor: "rgba(255,255,255,0.22)" },
                  }}
                  size="small"
                >
                  <ChevronLeftIcon sx={{ fontSize: 22 }} />
                </IconButton>
                {currentSlide
                  ? renderSlide(currentSlide, `c-${carouselIdx}`)
                  : null}
                <IconButton
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setCarouselIndex((i) => (i + 1) % n);
                  }}
                  aria-label="Next slide"
                  sx={{
                    position: "absolute",
                    right: 4,
                    top: "50%",
                    transform: "translateY(-50%)",
                    zIndex: 2,
                    color: "#fff",
                    bgcolor: "rgba(255,255,255,0.12)",
                    "&:hover": { bgcolor: "rgba(255,255,255,0.22)" },
                  }}
                  size="small"
                >
                  <ChevronRightIcon sx={{ fontSize: 22 }} />
                </IconButton>
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 8,
                    left: 0,
                    right: 0,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: 0.75,
                    zIndex: 2,
                  }}
                >
                  {slides.map((_, i) => (
                    <Box
                      key={`dash-carousel-dot-${i}`}
                      component="button"
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setCarouselIndex(i);
                      }}
                      aria-label={`Go to slide ${i + 1}`}
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        border: "none",
                        p: 0,
                        cursor: "pointer",
                        bgcolor:
                          i === carouselIdx ? "#fff" : "rgba(255,255,255,0.35)",
                        "&:hover": {
                          bgcolor:
                            i === carouselIdx
                              ? "#fff"
                              : "rgba(255,255,255,0.55)",
                        },
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Box>

          <Box
            sx={{
              position: "relative",
              p: 3,
              pr: thumbUiVisible ? { xs: 2, sm: 15 } : 3,
              pb: thumbUiVisible ? { xs: 2, sm: 2.5 } : 3,
              bgcolor: "#fafafa",
              borderTop: "1px solid #E5E7EB",
              overflow: "visible",
            }}
          >
            <Typography
              sx={{
                fontFamily: "Roboto, sans-serif",
                fontSize: 12,
                color: "#6B7280",
                textTransform: "uppercase",
                letterSpacing: "0.02em",
                mb: 1,
              }}
            >
              Details
            </Typography>
            <Box
              component="dl"
              sx={{
                m: 0,
                display: "grid",
                gridTemplateColumns: "88px 1fr",
                gap: 0.5,
                rowGap: 0.75,
              }}
            >
              {[
                ["When", formatDetailInfoTimestamp(row.at)],
                ["Camera", row.sourceName || "—"],
                ["Zone", row.zone || "—"],
                ["Status", effectiveRowStatus || "—"],
              ].map(([k, v]) => (
                <React.Fragment key={k}>
                  <Typography
                    component="dt"
                    sx={{
                      fontFamily: "Roboto, sans-serif",
                      fontSize: 13,
                      color: "#6B7280",
                    }}
                  >
                    {k}
                  </Typography>
                  <Typography
                    component="dd"
                    sx={{
                      fontFamily: "Roboto, sans-serif",
                      fontSize: 13,
                      color: "#212121",
                      m: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      pr: thumbUiVisible ? 0.5 : 0,
                    }}
                  >
                    {v}
                  </Typography>
                </React.Fragment>
              ))}
            </Box>

            {showDetailsThumbnail && thumbUrl ? (
              <Box
                component="button"
                type="button"
                onClick={() => {
                  if (!detailThumbLoaded) return;
                  if (n >= 2 && thumbnailSlideIndex >= 0) {
                    setCarouselIndex(thumbnailSlideIndex);
                    requestAnimationFrame(() => {
                      mediaAreaRef.current?.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                      });
                    });
                  } else {
                    setImageLightboxHref(thumbUrl);
                  }
                }}
                aria-label="Detection crop thumbnail — open in viewer"
                sx={{
                  position: "absolute",
                  bottom: 12,
                  right: 12,
                  zIndex: 2,
                  m: 0,
                  p: 0,
                  minWidth: { xs: 88, sm: 112 },
                  minHeight: { xs: 60, sm: 76 },
                  border:
                    n >= 2 &&
                    thumbnailSlideIndex >= 0 &&
                    carouselIdx === thumbnailSlideIndex
                      ? "2px solid #2932E5"
                      : "1px solid rgba(0,0,0,0.18)",
                  cursor: detailThumbLoaded ? "pointer" : "default",
                  borderRadius: "10px",
                  bgcolor: "#fff",
                  boxShadow:
                    "0 2px 10px rgba(0,0,0,0.14), 0 0 0 1px rgba(0,0,0,0.04)",
                  overflow: "hidden",
                  lineHeight: 0,
                  display: detailThumbLoaded ? "block" : "none",
                  pointerEvents: detailThumbLoaded ? "auto" : "none",
                  "&:focus-visible": {
                    outline: "2px solid #2932E5",
                    outlineOffset: 2,
                  },
                  "&:hover": { borderColor: "#2932E5" },
                }}
              >
                <AlertHistoryMediaImage
                  href={thumbUrl}
                  alt="Detection thumbnail"
                  onLoad={() => setDetailThumbLoaded(true)}
                  onError={() => setDetailThumbLoaded(false)}
                  imgSx={{
                    display: "block",
                    width: { xs: 80, sm: 104 },
                    height: { xs: 56, sm: 72 },
                    objectFit: "cover",
                    objectPosition: "center",
                  }}
                />
              </Box>
            ) : null}
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            justifyContent: "flex-end",
            px: 2.5,
            py: 2,
            backgroundColor: "#EEEEEE",
            borderTop: "1px solid #E0E0E0",
            gap: 1.5,
          }}
        >
          <Button
            variant="contained"
            disabled={acknowledgeDisabled}
            onClick={async (e) => {
              e.preventDefault();
              if (acknowledgeDisabled || !row) return;
              setStatusPatchBusy(true);
              try {
                await onAcknowledge(row);
              } finally {
                setStatusPatchBusy(false);
              }
            }}
            sx={{
              textTransform: "none",
              fontFamily: "Roboto, sans-serif",
              fontWeight: 600,
              borderRadius: "8px",
              px: 2.5,
              py: 1,
              backgroundColor: "#00796B",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "#00695C",
                boxShadow: "none",
              },
            }}
          >
            {ackButtonLabel}
          </Button>
          <Button
            variant="contained"
            disabled={closeWorkspaceDisabled}
            onClick={async (e) => {
              e.preventDefault();
              if (closeWorkspaceDisabled || !row) return;
              setStatusPatchBusy(true);
              try {
                await onMarkClosed(row);
              } finally {
                setStatusPatchBusy(false);
              }
            }}
            sx={{
              textTransform: "none",
              fontFamily: "Roboto, sans-serif",
              fontWeight: 600,
              borderRadius: "8px",
              px: 2.5,
              py: 1,
              backgroundColor: "#3949AB",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "#303F9F",
                boxShadow: "none",
              },
            }}
          >
            {closeButtonLabel}
          </Button>
        </DialogActions>
      </Dialog>

      {imageLightboxHref ? (
        <Box
          role="dialog"
          aria-modal="true"
          aria-label="Enlarged image"
          onClick={() => setImageLightboxHref(null)}
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 10050,
            bgcolor: "rgba(0,0,0,0.92)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: { xs: 1, sm: 2 },
          }}
        >
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{
              position: "relative",
              display: "inline-block",
              maxWidth: "min(98vw, 1800px)",
              maxHeight: "min(95vh, 1200px)",
            }}
          >
            <IconButton
              onClick={() => setImageLightboxHref(null)}
              aria-label="Close enlarged image"
              size="small"
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                zIndex: 2,
                color: "#fff",
                bgcolor: "rgba(0,0,0,0.55)",
                "&:hover": { bgcolor: "rgba(0,0,0,0.72)" },
              }}
            >
              <CloseIcon sx={{ fontSize: 22 }} />
            </IconButton>
            <AlertHistoryMediaImage
              href={imageLightboxHref}
              alt=""
              imgSx={{
                display: "block",
                maxWidth: "min(98vw, 1800px)",
                maxHeight: "min(95vh, 1200px)",
                width: "auto",
                height: "auto",
                objectFit: "contain",
                borderRadius: 1,
                boxShadow: "0 8px 40px rgba(0,0,0,0.45)",
              }}
            />
          </Box>
        </Box>
      ) : null}
    </>
  );
}

export function Dashboard() {
  /** Rules used to resolve action channels for streamed alert rows only (no mock table data). */
  const { alertRules } = useAlertData();
  const [dateRange] = useState("Last 7 Days");
  const [dashboardStats, setDashboardStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [useCaseId] = useState(null);
  const [sourceId] = useState(null);

  const [clock, setClock] = useState(() => new Date());
  const [tableSearch, setTableSearch] = useState("");
  const [viewMode, setViewMode] = useState("grid");
  /** Lower section: alert list table vs bar chart */
  const [tableGraphMode, setTableGraphMode] = useState("table");
  /** Same preset keys as Alert History (`GET /alerts/history`). */
  const [aiAlertsDateRange, setAiAlertsDateRange] = useState("today");
  const [aiCustomDialogOpen, setAiCustomDialogOpen] = useState(false);
  const [aiCustomStart, setAiCustomStart] = useState(null);
  const [aiCustomEnd, setAiCustomEnd] = useState(null);
  const prevAiDateRangeBeforeCustomRef = useRef("today");
  const [filterSource, setFilterSource] = useState([]);
  const [isSourceAllChecked, setIsSourceAllChecked] = useState(true);
  const [filterZone, setFilterZone] = useState([]);
  const [isZoneAllChecked, setIsZoneAllChecked] = useState(true);
  const [sourceFilterAnchor, setSourceFilterAnchor] = useState(null);
  const [zoneFilterAnchor, setZoneFilterAnchor] = useState(null);
  const [filterAlertType, setFilterAlertType] = useState([]);
  const [isAlertTypeAllChecked, setIsAlertTypeAllChecked] = useState(true);
  const [alertTypeFilterAnchor, setAlertTypeFilterAnchor] = useState(null);

  const [filterSeverity, setFilterSeverity] = useState([]);
  const [isSeverityAllChecked, setIsSeverityAllChecked] = useState(true);
  const [filterStatus, setFilterStatus] = useState([]);
  const [isStatusAllChecked, setIsStatusAllChecked] = useState(true);
  const [filterNotificationStatus, setFilterNotificationStatus] = useState([]);
  const [isNotificationStatusAllChecked, setIsNotificationStatusAllChecked] =
    useState(true);
  const [extraFilterAnchor, setExtraFilterAnchor] = useState(null);

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [actionAnchor, setActionAnchor] = useState(null);
  /** Cursor position for right-click context menu (viewport coords). */
  const [actionMenuPointerPosition, setActionMenuPointerPosition] =
    useState(null);
  /** Row key for the AI Alerts ⋮ menu (list + grid share one menu). */
  const [actionMenuRowKey, setActionMenuRowKey] = useState(null);
  const [localStatusOverrides, setLocalStatusOverrides] = useState({});
  const [aiGridImageErrors, setAiGridImageErrors] = useState({});
  const [sourceDetailRow, setSourceDetailRow] = useState(null);
  /** Use cases from GET `/use-cases/` for Alert Type dropdown (source of truth for names). */
  const [useCasesList, setUseCasesList] = useState([]);
  const [useCasesLoading, setUseCasesLoading] = useState(false);
  const [useCasesError, setUseCasesError] = useState(null);
  /** Source Name dropdown options from GET `/cameras` (`id`, `name`). */
  const [dashboardCameras, setDashboardCameras] = useState([]);
  const [dashboardCamerasLoading, setDashboardCamerasLoading] = useState(false);
  const [dashboardCamerasError, setDashboardCamerasError] = useState(null);
  /** Zone dropdown options from GET `/zones` (`id`, `camera_id`, `name`). */
  const [dashboardZones, setDashboardZones] = useState([]);
  const [dashboardZonesLoading, setDashboardZonesLoading] = useState(false);
  const [dashboardZonesError, setDashboardZonesError] = useState(null);

  const getPeriodForAPI = (range) => {
    const periodMap = {
      Today: "today",
      "Last 7 Days": "last_7_days",
      "Last 30 Days": "last_30_days",
      "This Month": "this_month",
    };
    return periodMap[range] || "last_7_days";
  };

  const refreshDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const params = { period: getPeriodForAPI(dateRange) };
      if (useCaseId) params.use_case_id = useCaseId;
      if (sourceId) params.source_id = sourceId;

      const response = await dashboardAPI.getStatistics(params);
      const dashboardData = unwrapDashboardStatisticsResponse(response);

      if (!dashboardData?.kpi_cards) {
        setDashboardStats(emptyDashboardStatisticsPayload());
      } else {
        setDashboardStats({
          ...dashboardData,
          trend_chart:
            dashboardData.trend_chart &&
            typeof dashboardData.trend_chart === "object"
              ? dashboardData.trend_chart
              : { data_points: [] },
        });
      }
    } catch (error) {
      console.error("Error fetching dashboard statistics:", error);
      setDashboardStats(emptyDashboardStatisticsPayload());
    } finally {
      setLoading(false);
    }
  }, [dateRange, useCaseId, sourceId]);

  useEffect(() => {
    refreshDashboard();
  }, [refreshDashboard]);

  useEffect(() => {
    const id = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const kpiData = useMemo(() => {
    const defaultRow = (color) => ({
      count: 0,
      critical: 0,
      percentage_change: 0,
      color,
    });
    const kpi = dashboardStats?.kpi_cards;
    if (!kpi || typeof kpi !== "object") {
      return {
        AI: defaultRow("#2932E5"),
        IoT: defaultRow("#FF9800"),
        System: defaultRow("#00C853"),
      };
    }
    if (
      kpi.ai_alert != null ||
      kpi.iot_alert != null ||
      kpi.system_alert != null
    ) {
      const ai =
        kpi.ai_alert && typeof kpi.ai_alert === "object" ? kpi.ai_alert : {};
      const iot =
        kpi.iot_alert && typeof kpi.iot_alert === "object" ? kpi.iot_alert : {};
      const sys =
        kpi.system_alert && typeof kpi.system_alert === "object"
          ? kpi.system_alert
          : {};
      return {
        AI: {
          count: Number(ai.count) || 0,
          critical: Math.max(0, Number(ai.critical) || 0),
          percentage_change: Number(ai.percentage_change) || 0,
          color: "#2932E5",
        },
        IoT: {
          count: Number(iot.count) || 0,
          critical: Math.max(0, Number(iot.critical) || 0),
          percentage_change: Number(iot.percentage_change) || 0,
          color: "#FF9800",
        },
        System: {
          count: Number(sys.count) || 0,
          critical: Math.max(0, Number(sys.critical) || 0),
          percentage_change: Number(sys.percentage_change) || 0,
          color: "#00C853",
        },
      };
    }
    if (kpi.alert_categories && typeof kpi.alert_categories === "object") {
      const c = kpi.alert_categories;
      const aiCount = c.alert?.count ?? 0;
      const iotCount = c.iot_alert?.count ?? 0;
      const sysCount = c.system_alert?.count ?? 0;
      return {
        AI: {
          count: aiCount,
          critical: criticalBadgeCount(aiCount),
          percentage_change: c.alert?.percentage_change ?? 0,
          color: "#2932E5",
        },
        IoT: {
          count: iotCount,
          critical: criticalBadgeCount(iotCount),
          percentage_change: c.iot_alert?.percentage_change ?? 0,
          color: "#FF9800",
        },
        System: {
          count: sysCount,
          critical: criticalBadgeCount(sysCount),
          percentage_change: c.system_alert?.percentage_change ?? 0,
          color: "#00C853",
        },
      };
    }
    return {
      AI: defaultRow("#2932E5"),
      IoT: defaultRow("#FF9800"),
      System: defaultRow("#00C853"),
    };
  }, [dashboardStats]);

  const barChartData = useMemo(
    () => [
      {
        name: "AI Cluster",
        total: kpiData.AI.count,
        critical: kpiData.AI.critical,
        totalFill: kpiData.AI.color,
      },
      {
        name: "IoT Nodes",
        total: kpiData.IoT.count,
        critical: kpiData.IoT.critical,
        totalFill: kpiData.IoT.color,
      },
      {
        name: "Network",
        total: kpiData.System.count,
        critical: kpiData.System.critical,
        totalFill: kpiData.System.color,
      },
    ],
    [kpiData],
  );

  const trendChartRows = useMemo(() => {
    const pts = dashboardStats?.trend_chart?.data_points;
    if (!Array.isArray(pts) || pts.length === 0) return [];

    const baseRows = pts.map((dp) => {
      const d = dp?.date;
      let name = "—";
      name = formatTrendChartDayLabel(d);
      const ai = Number(dp?.ai_alert?.count) || 0;
      const iot = Number(dp?.iot_alert?.count) || 0;
      const system = Number(dp?.system_alert?.count) || 0;
      const total = ai + iot + system;

      const aiCrit = Math.min(
        Math.max(0, Number(dp?.ai_alert?.critical) || 0),
        ai,
      );
      const iotCrit = Math.min(
        Math.max(0, Number(dp?.iot_alert?.critical) || 0),
        iot,
      );
      const sysCrit = Math.min(
        Math.max(0, Number(dp?.system_alert?.critical) || 0),
        system,
      );
      const critical = Math.min(aiCrit + iotCrit + sysCrit, total);

      const aiNc = Math.max(0, ai - aiCrit);
      const iotNc = Math.max(0, iot - iotCrit);
      const systemNc = Math.max(0, system - sysCrit);
      const alertsNonCritical = Math.max(0, aiNc + iotNc + systemNc);

      return {
        name,
        date: d,
        ai,
        iot,
        system,
        total,
        critical,
        aiNc,
        iotNc,
        systemNc,
        alertsNonCritical,
      };
    });

    const maxTotal = Math.max(1, ...baseRows.map((r) => r.total));
    /** Boost only tiny days so bars stay visible; large days use API values as-is. */
    const minVisibleStackTotal = Math.max(2, Math.ceil(maxTotal * 0.005));

    return baseRows.map((row) => {
      const scale =
        row.total > 0 && row.total < minVisibleStackTotal
          ? minVisibleStackTotal / row.total
          : 1;
      const stack = buildTrendStackDisplay(row, scale);

      return {
        ...row,
        stackTotalDisplay: row.total * scale,
        /** Chart-only stack heights; tooltip uses ai/iot/system/critical/aiNc. */
        ...stack,
      };
    });
  }, [dashboardStats]);

  /**
   * Today: GET /alerts/history (baseline) + GET /alerts/stream (live merge by id).
   * Other presets: paginated /alerts/history only.
   */
  const useStreamForAlertsTable = aiAlertsDateRange === "today";

  const selectedDashboardZoneRoiIds = useMemo(() => {
    if (isZoneAllChecked || filterZone.length === 0) return [];
    return [
      ...new Set(
        filterZone
          .map((id) => String(id ?? "").trim())
          .filter(Boolean),
      ),
    ];
  }, [isZoneAllChecked, filterZone]);

  const streamFiltersForToday = useMemo(() => {
    const filters = { period: "today" };
    const q = tableSearch.trim();
    if (q.length >= 2) {
      filters.search = q;
    }
    if (!isSeverityAllChecked && filterSeverity.length > 0) {
      const sev = [
        ...new Set(
          filterSeverity
            .map((s) => String(s).trim().toLowerCase())
            .filter(Boolean),
        ),
      ];
      if (sev.length > 0) filters.severity = sev.join(",");
    }
    if (!isStatusAllChecked && filterStatus.length > 0) {
      const st = [
        ...new Set(
          filterStatus
            .map((s) => dashboardWorkspaceStatusToApiStatus(s))
            .filter(Boolean),
        ),
      ];
      if (st.length > 0) filters.status = st.join(",");
    }
    if (!isNotificationStatusAllChecked && filterNotificationStatus.length > 0) {
      const ns = [
        ...new Set(
          filterNotificationStatus
            .map((v) => String(v).trim().toLowerCase())
            .filter(Boolean),
        ),
      ];
      if (ns.length > 0) filters.notification_status = ns.join(",");
    }
    if (!isZoneAllChecked && selectedDashboardZoneRoiIds.length > 0) {
      filters.roi_id = selectedDashboardZoneRoiIds.join(",");
    }
    if (!isSourceAllChecked && filterSource.length > 0) {
      const sourceNames = [
        ...new Set(
          filterSource
            .map((s) => String(s).trim())
            .filter(Boolean),
        ),
      ];
      if (sourceNames.length > 0) filters.source_name = sourceNames.join(",");
    }
    if (!isAlertTypeAllChecked && filterAlertType.length > 0) {
      const at = alertTypesParamForHistoryStream(filterAlertType);
      if (at) filters.alert_type = at;
    }
    return filters;
  }, [
    tableSearch,
    isSeverityAllChecked,
    filterSeverity,
    isStatusAllChecked,
    filterStatus,
    isNotificationStatusAllChecked,
    filterNotificationStatus,
    isZoneAllChecked,
    selectedDashboardZoneRoiIds,
    isSourceAllChecked,
    filterSource,
    isAlertTypeAllChecked,
    filterAlertType,
  ]);

  const todayStreamFilterSignature = useMemo(() => {
    const p = buildAlertsStreamSearchParams(streamFiltersForToday || {});
    const entries = Array.from(p.entries()).sort(([a], [b]) =>
      a.localeCompare(b),
    );
    return JSON.stringify(entries);
  }, [streamFiltersForToday]);

  const [todayBaselineById, setTodayBaselineById] = useState({});
  const [todayBaselineLoading, setTodayBaselineLoading] = useState(false);
  const [todayBaselineError, setTodayBaselineError] = useState(null);

  const [historyAlerts, setHistoryAlerts] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(null);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyRefreshToken, setHistoryRefreshToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const extractUseCases = (body) => {
      if (Array.isArray(body)) return body;
      if (body && typeof body === "object") {
        const arr = body.data ?? body.results ?? body.Data;
        if (Array.isArray(arr)) return arr;
      }
      return [];
    };

    const loadUseCases = async () => {
      setUseCasesLoading(true);
      setUseCasesError(null);
      try {
        const body = await useCasesAPI.list();
        if (cancelled) return;
        const arr = extractUseCases(body);
        setUseCasesList(arr);
      } catch (e) {
        if (!cancelled) {
          setUseCasesError(e instanceof Error ? e : new Error(String(e)));
          setUseCasesList([]);
        }
      } finally {
        if (!cancelled) setUseCasesLoading(false);
      }
    };

    void loadUseCases();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadZones = async () => {
      setDashboardZonesLoading(true);
      setDashboardZonesError(null);
      try {
        const body = await zonesAPI.list();
        if (cancelled) return;
        const arr = extractZonesArrayFromApi(body);
        const list = arr
          .filter((z) => z && typeof z === "object")
          .map((z) => ({
            id: String(z.id ?? "").trim(),
            cameraId: String(z.camera_id ?? z.cameraId ?? "").trim(),
            name: String(z.name ?? "").trim(),
          }))
          .filter((z) => z.id && z.name);
        setDashboardZones(list);
      } catch (e) {
        if (cancelled) return;
        setDashboardZonesError(e instanceof Error ? e : new Error(String(e)));
        setDashboardZones([]);
      } finally {
        if (!cancelled) setDashboardZonesLoading(false);
      }
    };

    void loadZones();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadCameras = async () => {
      setDashboardCamerasLoading(true);
      setDashboardCamerasError(null);
      try {
        const body = await camerasAPI.list();
        if (cancelled) return;
        const arr = extractCamerasArrayFromApi(body);
        const list = arr
          .filter((c) => c && typeof c === "object")
          .map((c) => ({
            id: String(c.id ?? c.camera_id ?? "").trim(),
            name: String(c.name ?? "").trim(),
          }))
          .filter((c) => c.id && c.name);
        setDashboardCameras(list);
      } catch (e) {
        if (cancelled) return;
        setDashboardCamerasError(
          e instanceof Error ? e : new Error(String(e)),
        );
        setDashboardCameras([]);
      } finally {
        if (!cancelled) setDashboardCamerasLoading(false);
      }
    };

    void loadCameras();
    return () => {
      cancelled = true;
    };
  }, []);

  const sseEnabled = Boolean(process.env.REACT_APP_API_URL);
  const { alertsById, connectionState, lastError } = useAlertStream(
    streamFiltersForToday,
    { enabled: sseEnabled && useStreamForAlertsTable },
  );

  useEffect(() => {
    if (!useStreamForAlertsTable || !sseEnabled) {
      setTodayBaselineById({});
      setTodayBaselineLoading(false);
      setTodayBaselineError(null);
      return undefined;
    }

    let cancelled = false;
    const applyBaselineFromResponse = (response) => {
      const { alertsArray } = extractAlertsHistoryPayload(response);
      const byId = {};
      for (const a of alertsArray) {
        const sid =
          a?.id != null
            ? String(a.id)
            : a?._id != null
              ? String(a._id)
              : null;
        if (sid) byId[sid] = a;
      }
      setTodayBaselineById(byId);
    };

    const loadTodayBaseline = async () => {
      setTodayBaselineLoading(true);
      setTodayBaselineError(null);
      const buildParams = () => {
        const p = buildAlertsStreamSearchParams(streamFiltersForToday);
        const params = {
          page: 1,
          per_page: TODAY_ALERTS_BASELINE_PER_PAGE,
        };
        for (const [k, v] of p.entries()) {
          params[k] = v;
        }
        if (!params.period) {
          params.period = "today";
        }
        return params;
      };

      try {
        const params = buildParams();
        let response;
        try {
          response = await alertsHistoryAPI.getHistory(params);
        } catch (firstErr) {
          if (
            params.search != null &&
            String(params.search).trim() !== ""
          ) {
            console.warn(
              "Dashboard today baseline: search request failed; retrying without server search:",
              firstErr,
            );
            const retryParams = { ...params };
            delete retryParams.search;
            response = await alertsHistoryAPI.getHistory(retryParams);
          } else {
            throw firstErr;
          }
        }
        if (cancelled) return;
        applyBaselineFromResponse(response);
      } catch (e) {
        if (!cancelled) {
          console.error("Dashboard today baseline (history) fetch failed:", e);
          setTodayBaselineError(e instanceof Error ? e : new Error(String(e)));
          setTodayBaselineById({});
        }
      } finally {
        if (!cancelled) {
          setTodayBaselineLoading(false);
        }
      }
    };

    const delayMs = tableSearch.trim().length >= 2 ? 400 : 0;
    const tid = setTimeout(() => {
      void loadTodayBaseline();
    }, delayMs);

    return () => {
      cancelled = true;
      clearTimeout(tid);
    };
  }, [
    useStreamForAlertsTable,
    sseEnabled,
    todayStreamFilterSignature,
    historyRefreshToken,
    tableSearch,
  ]);

  useEffect(() => {
    if (useStreamForAlertsTable) {
      setHistoryAlerts([]);
      setHistoryTotal(0);
      setHistoryLoading(false);
      setHistoryError(null);
      return undefined;
    }

    let cancelled = false;
    const load = async () => {
      setHistoryLoading(true);
      setHistoryError(null);
      try {
        const params = {
          page,
          per_page: rowsPerPage,
        };
        appendAiAlertsHistoryDateParams(
          params,
          aiAlertsDateRange,
          aiCustomStart,
          aiCustomEnd,
        );
        const q = tableSearch.trim();
        if (q.length >= 2) {
          params.search = q;
        }
        if (!isSeverityAllChecked && filterSeverity.length > 0) {
          const sev = [
            ...new Set(
              filterSeverity
                .map((s) => String(s).trim().toLowerCase())
                .filter(Boolean),
            ),
          ];
          if (sev.length > 0) params.severity = sev.join(",");
        }
        if (!isStatusAllChecked && filterStatus.length > 0) {
          const st = [
            ...new Set(
              filterStatus
                .map((s) => dashboardWorkspaceStatusToApiStatus(s))
                .filter(Boolean),
            ),
          ];
          if (st.length > 0) params.status = st.join(",");
        }
        if (
          !isNotificationStatusAllChecked &&
          filterNotificationStatus.length > 0
        ) {
          const ns = [
            ...new Set(
              filterNotificationStatus
                .map((v) => String(v).trim().toLowerCase())
                .filter(Boolean),
            ),
          ];
          if (ns.length > 0) params.notification_status = ns.join(",");
        }
        if (!isZoneAllChecked && selectedDashboardZoneRoiIds.length > 0) {
          params.roi_id = selectedDashboardZoneRoiIds.join(",");
        }
        if (!isSourceAllChecked && filterSource.length > 0) {
          const sourceNames = [
            ...new Set(
              filterSource
                .map((s) => String(s).trim())
                .filter(Boolean),
            ),
          ];
          if (sourceNames.length > 0) {
            params.source_name = sourceNames.join(",");
          }
        }
        if (!isAlertTypeAllChecked && filterAlertType.length > 0) {
          const at = alertTypesParamForHistoryStream(filterAlertType);
          if (at) params.alert_type = at;
        }

        let response;
        try {
          response = await alertsHistoryAPI.getHistory(params);
        } catch (firstErr) {
          if (
            params.search != null &&
            String(params.search).trim() !== ""
          ) {
            console.warn(
              "Dashboard history: search request failed; retrying without server search:",
              firstErr,
            );
            const retryParams = { ...params };
            delete retryParams.search;
            response = await alertsHistoryAPI.getHistory(retryParams);
          } else {
            throw firstErr;
          }
        }
        if (cancelled) return;

        const { alertsArray, total } = extractAlertsHistoryPayload(response);
        setHistoryAlerts(alertsArray);
        setHistoryTotal(typeof total === "number" ? total : alertsArray.length);
      } catch (e) {
        if (!cancelled) {
          console.error("Dashboard alert history fetch failed:", e);
          setHistoryError(e instanceof Error ? e : new Error(String(e)));
          setHistoryAlerts([]);
          setHistoryTotal(0);
        }
      } finally {
        if (!cancelled) {
          setHistoryLoading(false);
        }
      }
    };

    const delayMs = tableSearch.trim().length >= 2 ? 400 : 0;
    const tid = setTimeout(() => {
      void load();
    }, delayMs);

    return () => {
      cancelled = true;
      clearTimeout(tid);
    };
  }, [
    useStreamForAlertsTable,
    aiAlertsDateRange,
    aiCustomStart,
    aiCustomEnd,
    page,
    rowsPerPage,
    tableSearch,
    isSeverityAllChecked,
    filterSeverity,
    isStatusAllChecked,
    filterStatus,
    isNotificationStatusAllChecked,
    filterNotificationStatus,
    isZoneAllChecked,
    selectedDashboardZoneRoiIds,
    isSourceAllChecked,
    filterSource,
    isAlertTypeAllChecked,
    filterAlertType,
    historyRefreshToken,
  ]);

  /** REST baseline for today, overwritten / extended by SSE payloads (same id = merge, stream wins). */
  const mergedTodayAlertsById = useMemo(() => {
    const out = { ...todayBaselineById };
    for (const [id, payload] of Object.entries(alertsById)) {
      const prev = out[id];
      out[id] =
        prev && typeof prev === "object" && typeof payload === "object"
          ? mergeStreamAlertWithBaseline(prev, payload)
          : payload;
    }
    return out;
  }, [todayBaselineById, alertsById]);

  /** Resolve `source_name` when API only sends `source_id` (GET /cameras names). */
  const dashboardCameraIdToName = useMemo(() => {
    const m = new Map();
    for (const c of dashboardCameras) {
      const id = String(c?.id ?? "").trim();
      const name = String(c?.name ?? "").trim();
      if (id && name) m.set(id, name);
    }
    return m;
  }, [dashboardCameras]);

  /**
   * When server-side filters are applied, trust the API row set for dates — otherwise the
   * "Today + stream" mode strips rows older than local midnight and hides valid API results.
   */
  const skipStreamCalendarTodayClientFilter = useMemo(
    () =>
      (!isSourceAllChecked && filterSource.length > 0) ||
      (!isZoneAllChecked && filterZone.length > 0) ||
      (!isAlertTypeAllChecked && filterAlertType.length > 0) ||
      (!isSeverityAllChecked && filterSeverity.length > 0) ||
      (!isStatusAllChecked && filterStatus.length > 0) ||
      (!isNotificationStatusAllChecked && filterNotificationStatus.length > 0),
    [
      isSourceAllChecked,
      filterSource.length,
      isZoneAllChecked,
      filterZone.length,
      isAlertTypeAllChecked,
      filterAlertType.length,
      isSeverityAllChecked,
      filterSeverity.length,
      isStatusAllChecked,
      filterStatus.length,
      isNotificationStatusAllChecked,
      filterNotificationStatus.length,
    ],
  );

  const streamTableRows = useMemo(() => {
    const rows = Object.values(mergedTodayAlertsById)
      .map((a) =>
        mapAlertResponseToDashboardRow(a, alertRules, dashboardCameraIdToName),
      )
      .filter(Boolean);
    rows.sort((a, b) => {
      const ta =
        a.at instanceof Date ? a.at.getTime() : new Date(a.at).getTime();
      const tb =
        b.at instanceof Date ? b.at.getTime() : new Date(b.at).getTime();
      return tb - ta;
    });
    return rows;
  }, [mergedTodayAlertsById, alertRules, dashboardCameraIdToName]);

  const historyTableRows = useMemo(() => {
    const rows = historyAlerts
      .map((a) =>
        mapAlertResponseToDashboardRow(a, alertRules, dashboardCameraIdToName),
      )
      .filter(Boolean);
    rows.sort((a, b) => {
      const ta =
        a.at instanceof Date ? a.at.getTime() : new Date(a.at).getTime();
      const tb =
        b.at instanceof Date ? b.at.getTime() : new Date(b.at).getTime();
      return tb - ta;
    });
    return rows;
  }, [historyAlerts, alertRules, dashboardCameraIdToName]);

  /** Today: history baseline + stream merge. Other presets: GET /alerts/history page. */
  const allRows = useMemo(
    () => (useStreamForAlertsTable ? streamTableRows : historyTableRows),
    [useStreamForAlertsTable, streamTableRows, historyTableRows],
  );

  const aiAlertsEmptyMessage = useMemo(() => {
    if (!useStreamForAlertsTable && historyLoading) {
      return "Loading alert history…";
    }
    if (!useStreamForAlertsTable && historyError) {
      return "Could not load alert history. Try again or change filters.";
    }
    if (
      useStreamForAlertsTable &&
      todayBaselineLoading &&
      streamTableRows.length === 0
    ) {
      return "Loading today's alerts…";
    }
    if (
      useStreamForAlertsTable &&
      todayBaselineError &&
      streamTableRows.length === 0
    ) {
      return "Could not load today's alerts. Live stream may still connect.";
    }
    if (allRows.length === 0) return "Data is not available";
    return "No alerts match the selected filters";
  }, [
    allRows.length,
    useStreamForAlertsTable,
    historyLoading,
    historyError,
    todayBaselineLoading,
    todayBaselineError,
    streamTableRows.length,
  ]);

  const zoneFilterOptions = useMemo(() => {
    const out = [];
    const seen = new Set();
    const cameraById = new Map();
    for (const c of dashboardCameras) {
      const id = String(c?.id ?? "").trim();
      const name = String(c?.name ?? "").trim();
      if (id && name && !cameraById.has(id)) {
        cameraById.set(id, name);
      }
    }
    for (const zoneItem of dashboardZones) {
      const id = String(zoneItem?.id ?? "").trim();
      const name = String(zoneItem?.name ?? "").trim();
      const cameraId = String(zoneItem?.cameraId ?? "").trim();
      if (!id || !name || seen.has(id)) continue;
      seen.add(id);
      const cameraName = cameraId ? cameraById.get(cameraId) || "" : "";
      const label = cameraName ? `${cameraName} ${name}` : name;
      out.push({ value: id, label, zoneName: name });
    }
    out.sort((a, b) => String(a.label).localeCompare(String(b.label)));
    return out;
  }, [dashboardZones, dashboardCameras]);

  const selectedZoneNames = useMemo(() => {
    const out = new Set();
    for (const zid of filterZone) {
      const id = String(zid ?? "").trim();
      if (!id) continue;
      const hit = zoneFilterOptions.find((z) => z.value === id);
      if (hit?.zoneName) out.add(hit.zoneName);
    }
    return out;
  }, [filterZone, zoneFilterOptions]);

  const sources = useMemo(() => {
    const s = new Set();
    for (const cameraItem of dashboardCameras) {
      const v = String(cameraItem?.name ?? "").trim();
      if (v) s.add(v);
    }
    for (const r of allRows) {
      const v = r?.sourceName != null ? String(r.sourceName).trim() : "";
      if (v) s.add(v);
    }
    // Keep currently selected values visible even while data/date window changes.
    for (const sel of filterSource) {
      const v = String(sel ?? "").trim();
      if (v) s.add(v);
    }
    return ["", ...Array.from(s).sort((a, b) => a.localeCompare(b))];
  }, [dashboardCameras, allRows, filterSource]);

  /**
   * Alert Type dropdown: prefer use-case UUID (server `alert_type`), fall back to display
   * name from rules or from loaded rows so the list is never empty when alerts exist.
   */
  const alertTypeFilterOptions = useMemo(() => {
    const out = [];
    const seen = new Set();

    const addOption = (valueRaw, labelRaw) => {
      const value = valueRaw != null ? String(valueRaw).trim() : "";
      const label = labelRaw != null ? String(labelRaw).trim() : "";
      if (!value || !label || seen.has(value)) return;
      seen.add(value);
      out.push({ value, label });
    };

    // Primary source: use cases from API.
    for (const uc of Array.isArray(useCasesList) ? useCasesList : []) {
      const id = uc?.id != null ? String(uc.id).trim() : "";
      const name = uc?.name != null ? String(uc.name).trim() : "";
      if (!id || !name) continue;
      if (!isAlertHistoryUsecaseId(id)) continue;
      addOption(id, name);
    }

    // Include alert types seen in currently loaded rows (covers newly incoming data).
    for (const row of allRows) {
      const id = row?.useCaseId != null ? String(row.useCaseId).trim() : "";
      const label = row?.alertType != null ? String(row.alertType).trim() : "";
      if (!id || !label || !isAlertHistoryUsecaseId(id)) continue;
      addOption(id, label);
    }

    // Ensure selected values remain visible while date/data windows change.
    for (const sel of filterAlertType) {
      const value = String(sel ?? "").trim();
      if (!value || !isAlertHistoryUsecaseId(value) || seen.has(value)) continue;
      const fromRow = allRows.find(
        (r) => String(r.useCaseId || "").trim() === value,
      );
      const fromUseCase = (Array.isArray(useCasesList) ? useCasesList : []).find(
        (uc) => String(uc?.id ?? "").trim() === value,
      );
      const label =
        (fromUseCase?.name != null && String(fromUseCase.name).trim()) ||
        (fromRow?.alertType != null && String(fromRow.alertType).trim()) ||
        value;
      addOption(value, label);
    }

    out.sort((a, b) => String(a.label).localeCompare(String(b.label)));
    return out;
  }, [useCasesList, allRows, filterAlertType]);

  /** Toolbar label for multi-select Alert Type (UUIDs → human labels). */
  const getAiDashboardAlertTypeToolbarDisplay = useCallback(() => {
    if (isAlertTypeAllChecked) return "Alert types";
    if (!filterAlertType.length) return "No alert types";
    if (filterAlertType.length === 1) {
      const v = filterAlertType[0];
      const opt = alertTypeFilterOptions.find((o) => o.value === v);
      if (opt?.label) return opt.label;
      const fromRow = allRows.find(
        (r) => String(r.useCaseId || "") === String(v),
      );
      if (fromRow?.alertType) return String(fromRow.alertType);
      if (isAlertHistoryUsecaseId(v)) return "Alert type";
      return String(v);
    }
    return `${filterAlertType.length} selected`;
  }, [isAlertTypeAllChecked, filterAlertType, alertTypeFilterOptions, allRows]);

  const getSourceFilterToolbarDisplay = useCallback(() => {
    if (isSourceAllChecked) return "Sub-sources";
    if (filterSource.length === 0) return "No sub-sources";
    if (filterSource.length === 1) return filterSource[0];
    return `${filterSource.length} selected`;
  }, [isSourceAllChecked, filterSource]);

  const getZoneFilterToolbarDisplay = useCallback(() => {
    if (isZoneAllChecked) return "Zones";
    if (filterZone.length === 0) return "No zones";
    if (filterZone.length === 1) {
      const hit = zoneFilterOptions.find((z) => z.value === filterZone[0]);
      return hit?.label ?? String(filterZone[0]);
    }
    return `${filterZone.length} selected`;
  }, [isZoneAllChecked, filterZone, zoneFilterOptions]);

  const severityOptions = useMemo(() => {
    const seen = new Set(
      AI_ALERTS_SEVERITY_FILTER_LEVELS.map((x) => x.toLowerCase()),
    );
    const extras = [];
    for (const r of allRows) {
      const v = r?.severity;
      if (v == null || String(v).trim() === "") continue;
      const str = String(v).trim();
      const key = str.toLowerCase();
      if (!seen.has(key)) {
        seen.add(key);
        extras.push(str);
      }
    }
    extras.sort((a, b) => String(a).localeCompare(String(b)));
    return [...AI_ALERTS_SEVERITY_FILTER_LEVELS, ...extras];
  }, [allRows]);

  const statusOptions = useMemo(() => {
    const s = new Set(["Active", "Acknowledged", "Closed"]);
    for (const r of allRows) {
      const st = getEffectiveDashboardRowStatus(r, localStatusOverrides);
      if (st != null && String(st) !== "") s.add(st);
    }
    return Array.from(s).sort((a, b) => String(a).localeCompare(String(b)));
  }, [allRows, localStatusOverrides]);

  const hasExtraFilters = Boolean(
    !isSeverityAllChecked ||
      !isStatusAllChecked ||
      !isNotificationStatusAllChecked,
  );

  const hasActiveFilters = Boolean(
    tableSearch.trim() ||
      aiAlertsDateRange !== "today" ||
      aiCustomStart ||
      aiCustomEnd ||
      !isSourceAllChecked ||
      filterSource.length > 0 ||
      !isZoneAllChecked ||
      filterZone.length > 0 ||
      !isAlertTypeAllChecked ||
      filterAlertType.length > 0 ||
      hasExtraFilters,
  );

  const handleResetFilters = useCallback(() => {
    setTableSearch("");
    setAiAlertsDateRange("today");
    setAiCustomStart(null);
    setAiCustomEnd(null);
    setFilterSource([]);
    setIsSourceAllChecked(true);
    setFilterZone([]);
    setIsZoneAllChecked(true);
    setFilterAlertType([]);
    setIsAlertTypeAllChecked(true);
    setFilterSeverity([]);
    setIsSeverityAllChecked(true);
    setFilterStatus([]);
    setIsStatusAllChecked(true);
    setFilterNotificationStatus([]);
    setIsNotificationStatusAllChecked(true);
    setPage(1);
  }, []);

  const filteredRows = useMemo(() => {
    const q = tableSearch.trim().toLowerCase();
    const now = new Date();
    const startOfToday = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
    );

    return allRows.filter((row) => {
      if (useStreamForAlertsTable && !skipStreamCalendarTodayClientFilter) {
        const t = row.at instanceof Date ? row.at : new Date(row.at);
        if (Number.isNaN(t.getTime()) || t < startOfToday) return false;
      }
      if (q.length >= 2 && !dashboardRowMatchesSearchQuery(row, q, localStatusOverrides)) {
        return false;
      }
      if (!isSourceAllChecked && filterSource.length > 0) {
        if (!filterSource.includes(row.sourceName)) return false;
      }
      if (!isZoneAllChecked && filterZone.length > 0) {
        const rowRoiId = String(row?.roiId ?? "").trim();
        if (rowRoiId) {
          if (!filterZone.includes(rowRoiId)) return false;
        } else {
          const rowZoneName = String(row?.zone ?? "").trim();
          if (!rowZoneName || !selectedZoneNames.has(rowZoneName)) return false;
        }
      }
      if (!isAlertTypeAllChecked && filterAlertType.length > 0) {
        const matches = filterAlertType.some((sel) => {
          if (isAlertHistoryUsecaseId(sel)) {
            return String(row.useCaseId || "") === String(sel);
          }
          return row.alertType === sel;
        });
        if (!matches) return false;
      }
      if (!isSeverityAllChecked && filterSeverity.length > 0) {
        const sevOk = filterSeverity.some(
          (sel) => String(row.severity) === String(sel),
        );
        if (!sevOk) return false;
      }
      if (!isStatusAllChecked && filterStatus.length > 0) {
        const rowSt = String(
          getEffectiveDashboardRowStatus(row, localStatusOverrides),
        );
        const stOk = filterStatus.some((sel) => rowSt === String(sel));
        if (!stOk) return false;
      }
      if (
        !isNotificationStatusAllChecked &&
        filterNotificationStatus.length > 0
      ) {
        const nsRaw =
          row.notificationStatus != null ? String(row.notificationStatus) : "";
        const nsLower = nsRaw.trim().toLowerCase();
        const nsOk = filterNotificationStatus.some(
          (sel) => nsLower === String(sel).trim().toLowerCase(),
        );
        if (!nsOk) return false;
      }
      return true;
    });
  }, [
    allRows,
    localStatusOverrides,
    tableSearch,
    isSourceAllChecked,
    filterSource,
    isZoneAllChecked,
    filterZone,
    isAlertTypeAllChecked,
    selectedZoneNames,
    filterAlertType,
    useStreamForAlertsTable,
    isSeverityAllChecked,
    filterSeverity,
    isStatusAllChecked,
    filterStatus,
    isNotificationStatusAllChecked,
    filterNotificationStatus,
  ]);

  useEffect(() => {
    setPage(1);
  }, [
    tableSearch,
    isSourceAllChecked,
    filterSource,
    isZoneAllChecked,
    filterZone,
    isAlertTypeAllChecked,
    filterAlertType,
    aiAlertsDateRange,
    aiCustomStart,
    aiCustomEnd,
    isSeverityAllChecked,
    filterSeverity,
    isStatusAllChecked,
    filterStatus,
    isNotificationStatusAllChecked,
    filterNotificationStatus,
  ]);

  /** History responses are already paginated by the server; Today stream is client-paged. */
  const pagedRows = useMemo(() => {
    if (!useStreamForAlertsTable) {
      return filteredRows;
    }
    const start = (page - 1) * rowsPerPage;
    return filteredRows.slice(start, start + rowsPerPage);
  }, [useStreamForAlertsTable, filteredRows, page, rowsPerPage]);

  const actionMenuRow = useMemo(() => {
    if (actionMenuRowKey == null) return null;
    return (
      filteredRows.find((r) => r.rowKey === actionMenuRowKey) ?? null
    );
  }, [filteredRows, actionMenuRowKey]);

  const closeAiAlertActionMenu = useCallback(() => {
    setActionAnchor(null);
    setActionMenuPointerPosition(null);
    setActionMenuRowKey(null);
  }, []);

  const patchDashboardRowWorkspaceStatus = useCallback(
    async (rowKey, apiStatus, uiStatus) => {
      if (rowKey == null) return false;
      try {
        await alertsHistoryAPI.patchAlertStatus(rowKey, apiStatus);
        setLocalStatusOverrides((prev) => ({
          ...prev,
          [String(rowKey)]: uiStatus,
        }));
        setHistoryRefreshToken((t) => t + 1);
        return true;
      } catch (e) {
        console.error("Update alert status failed:", e);
        return false;
      }
    },
    [],
  );

  const handleDashboardRowAcknowledge = useCallback(async () => {
    if (actionMenuRowKey == null) return;
    const ok = await patchDashboardRowWorkspaceStatus(
      actionMenuRowKey,
      "acknowledged",
      "Acknowledged",
    );
    if (ok) closeAiAlertActionMenu();
  }, [
    actionMenuRowKey,
    patchDashboardRowWorkspaceStatus,
    closeAiAlertActionMenu,
  ]);

  const handleDashboardRowClose = useCallback(async () => {
    if (actionMenuRowKey == null) return;
    const ok = await patchDashboardRowWorkspaceStatus(
      actionMenuRowKey,
      "closed",
      "Closed",
    );
    if (ok) closeAiAlertActionMenu();
  }, [
    actionMenuRowKey,
    patchDashboardRowWorkspaceStatus,
    closeAiAlertActionMenu,
  ]);

  const handleDetailDialogAcknowledge = useCallback(
    async (r) => {
      if (!r?.rowKey) return;
      await patchDashboardRowWorkspaceStatus(
        r.rowKey,
        "acknowledged",
        "Acknowledged",
      );
    },
    [patchDashboardRowWorkspaceStatus],
  );

  const handleDetailDialogMarkClosed = useCallback(
    async (r) => {
      if (!r?.rowKey) return;
      await patchDashboardRowWorkspaceStatus(r.rowKey, "closed", "Closed");
    },
    [patchDashboardRowWorkspaceStatus],
  );

  const actionMenuEffectiveStatus = actionMenuRow
    ? getEffectiveDashboardRowStatus(actionMenuRow, localStatusOverrides)
    : "Active";

  useEffect(() => {
    closeAiAlertActionMenu();
  }, [page, closeAiAlertActionMenu]);

  const paginationTotal = useStreamForAlertsTable
    ? filteredRows.length
    : historyTotal;

  const clockLabel = clock.toLocaleString("en-US", {
    weekday: "short",
    month: "short",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });

  const aiAlertsDateSelectDisplay = useMemo(() => {
    if (
      aiAlertsDateRange === "custom" &&
      aiCustomStart &&
      aiCustomEnd &&
      !Number.isNaN(aiCustomStart.getTime()) &&
      !Number.isNaN(aiCustomEnd.getTime())
    ) {
      const opts = { month: "short", day: "numeric", year: "numeric" };
      return `${aiCustomStart.toLocaleDateString("en-US", opts)} – ${aiCustomEnd.toLocaleDateString("en-US", opts)}`;
    }
    return aiAlertsDatePresetLabel(aiAlertsDateRange);
  }, [aiAlertsDateRange, aiCustomStart, aiCustomEnd]);

  const handleAiAlertsDateRangeChange = useCallback(
    (e) => {
      const v = e.target.value;
      if (v === "custom") {
        if (aiAlertsDateRange !== "custom") {
          prevAiDateRangeBeforeCustomRef.current = aiAlertsDateRange;
        }
        setAiCustomDialogOpen(true);
        setAiAlertsDateRange("custom");
        return;
      }
      setAiCustomStart(null);
      setAiCustomEnd(null);
      setAiAlertsDateRange(v);
    },
    [aiAlertsDateRange],
  );

  const handleAiCustomRangeApply = useCallback((start, end) => {
    if (!start || !end) return;
    const s = start instanceof Date ? start : new Date(start);
    const e = end instanceof Date ? end : new Date(end);
    if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime()) || s > e) return;
    setAiCustomStart(s);
    setAiCustomEnd(e);
    setAiCustomDialogOpen(false);
    setHistoryRefreshToken((t) => t + 1);
  }, []);

  const handleAiCustomRangeCancel = useCallback(() => {
    setAiCustomDialogOpen(false);
    if (
      aiAlertsDateRange === "custom" &&
      (!aiCustomStart || !aiCustomEnd)
    ) {
      setAiAlertsDateRange(prevAiDateRangeBeforeCustomRef.current);
    }
  }, [aiAlertsDateRange, aiCustomStart, aiCustomEnd]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ px: 2, backgroundColor: "#F0F0F0", minHeight: "100%" }}>
      <Box
        sx={{
          backgroundColor: "#FFFFFF",
          border: "1px solid #E0E0E0",
          borderRadius: "12px",
          p: { xs: 2, sm: 2.5 },

          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.06)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
            mb: 2,
          }}
        >
          <Typography
            sx={{
              fontFamily: "Roboto, sans-serif",
              fontWeight: 600,
              fontSize: "18px",
              color: "#212121",
            }}
          >
            Alert Overview
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                px: 2,
                py: 0.5,
                borderRadius: "20px",
                backgroundColor: "#FFF3E0",
                border: "1px solid #FFE0B2",
              }}
            >
              <Typography
                component="span"
                sx={{
                  fontFamily: "Roboto, sans-serif",
                  fontSize: "13px",
                  color: "#E65100",
                }}
              >
                {clockLabel}
              </Typography>
              <IconButton
                size="small"
                onClick={() => {
                  refreshDashboard();
                  setHistoryRefreshToken((x) => x + 1);
                }}
                sx={{
                  p: 0.35,
                  color: "#E65100",
                  "&:hover": { backgroundColor: "rgba(230, 81, 0, 0.08)" },
                }}
                aria-label="Refresh dashboard"
              >
                <RefreshIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>

            <Box
              component="button"
              type="button"
              role="switch"
              aria-checked={tableGraphMode === "graph"}
              aria-label={
                tableGraphMode === "table"
                  ? "Table view selected, switch to graph"
                  : "Graph view selected, switch to table"
              }
              onClick={() =>
                setTableGraphMode((m) => (m === "table" ? "graph" : "table"))
              }
              sx={{
                position: "relative",
                width: 96,
                height: 34,
                flexShrink: 0,
                border: "none",
                padding: 0,
                cursor: "pointer",
                borderRadius: 17,
                backgroundColor:
                  tableGraphMode === "table" ? "#2932E5" : "#E53935",
                transition: "background-color 0.28s ease",
                boxShadow:
                  tableGraphMode === "table"
                    ? "0 1px 4px rgba(41, 50, 229, 0.28)"
                    : "0 1px 4px rgba(229, 57, 53, 0.28)",
                "&:hover": {
                  filter: "brightness(1.05)",
                },
                "&:focus-visible": {
                  outline: "2px solid #2932E5",
                  outlineOffset: 2,
                },
              }}
            >
              <Typography
                sx={{
                  position: "absolute",
                  left: 10,
                  top: "50%",
                  transform: "translateY(-50%)",
                  fontFamily: "Roboto, sans-serif",
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "#FFFFFF",
                  letterSpacing: "0.02em",
                  opacity: tableGraphMode === "table" ? 1 : 0,
                  transition: "opacity 0.2s ease",
                  pointerEvents: "none",
                  zIndex: 1,
                }}
              >
                Table
              </Typography>
              <Typography
                sx={{
                  position: "absolute",
                  right: 10,
                  top: "50%",
                  transform: "translateY(-50%)",
                  fontFamily: "Roboto, sans-serif",
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "#FFFFFF",
                  letterSpacing: "0.02em",
                  opacity: tableGraphMode === "graph" ? 1 : 0,
                  transition: "opacity 0.2s ease",
                  pointerEvents: "none",
                  zIndex: 1,
                }}
              >
                Graph
              </Typography>
              <Box
                aria-hidden
                sx={{
                  position: "absolute",
                  top: 4,
                  left: 4,
                  width: 26,
                  height: 26,
                  borderRadius: "50%",
                  backgroundColor: "#FFFFFF",
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.2)",
                  transform:
                    tableGraphMode === "table"
                      ? "translateX(62px)"
                      : "translateX(0)",
                  transition: "transform 0.28s cubic-bezier(0.4, 0, 0.2, 1)",
                  pointerEvents: "none",
                  zIndex: 2,
                }}
              />
            </Box>
          </Box>
        </Box>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { key: "AI", label: "AI Alerts" },
            {
              key: "IoT",
              label: "IoT Alerts",
              upcoming: true,
            },
            {
              key: "System",
              label: "System Alerts",
              upcoming: true,
            },
          ].map(({ key, label, upcoming = false }) => {
            const info = kpiData[key];
            const crit = info.critical;
            return (
              <Grid item xs={12} md={4} key={key}>
                <Box
                  title={upcoming ? "Coming soon" : undefined}
                  sx={{ height: "100%", borderRadius: "12px" }}
                >
                  <Card
                    aria-disabled={upcoming}
                    sx={{
                      borderRadius: "12px",
                      backgroundColor: info.color,
                      boxShadow: "0px 4px 12px rgba(0,0,0,0.12)",
                      color: "#fff",
                      overflow: "visible",
                      cursor: upcoming ? "default" : "pointer",
                      opacity: upcoming ? 0.62 : 1,
                      filter: upcoming ? "grayscale(0.45)" : "none",
                      pointerEvents: upcoming ? "none" : "auto",
                      transformOrigin: "center center",
                      transition:
                        "transform 0.22s ease, box-shadow 0.22s ease, filter 0.22s ease, opacity 0.2s ease",
                      ...(upcoming
                        ? {}
                        : {
                            "&:hover": {
                              transform: "scale(1.03) translateY(-4px)",
                              boxShadow: "0px 12px 28px rgba(0, 0, 0, 0.22)",
                              filter: "brightness(1.06)",
                            },
                          }),
                    }}
                  >
                  <CardContent
                    sx={{ p: "24px !important", position: "relative" }}
                  >
                    <Chip
                      label={upcoming ? "Upcoming Feature" : `${crit} critical`}
                      size="small"
                      sx={{
                        position: "absolute",
                        top: 12,
                        right: 12,
                        backgroundColor: upcoming
                          ? "rgba(255, 255, 255, 0.92)"
                          : "#F48FB1",
                        color: upcoming ? "rgba(0, 0, 0, 0.65)" : "#880E4F",
                        fontWeight: 600,
                        fontSize: "11px",
                        height: "24px",
                      }}
                    />
                    <Typography
                      sx={{
                        fontFamily: "Roboto, sans-serif",
                        fontSize: "42px",
                        fontWeight: 700,
                        lineHeight: 1.1,
                        mt: 1,
                      }}
                    >
                      {info.count}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: "Roboto, sans-serif",
                        fontSize: "16px",
                        fontWeight: 500,
                        opacity: 0.95,
                        mt: 0.5,
                      }}
                    >
                      {label}
                    </Typography>
                    {Number.isFinite(info.percentage_change) ? (
                      <Typography
                        sx={{
                          fontFamily: "Roboto, sans-serif",
                          fontSize: "13px",
                          fontWeight: 500,
                          opacity: 0.88,
                          mt: 0.75,
                        }}
                      >
                        {info.percentage_change >= 0 ? "↑" : "↓"}{" "}
                        {Math.abs(info.percentage_change)}% vs prior
                      </Typography>
                    ) : null}
                  </CardContent>
                </Card>
                </Box>
              </Grid>
            );
          })}
        </Grid>

        <Card
          sx={{
            borderRadius: "10px",
            border: "1px solid #E8E8E8",
            boxShadow: "none",
            overflow: "hidden",
            backgroundColor: "#FFFFFF",
          }}
        >
          <CardContent
            sx={{
              p: tableGraphMode === "graph" ? 2 : "1px !important",
            }}
          >
            {tableGraphMode === "graph" ? (
              <AlertGraphPanel
                aggregateData={barChartData}
                trendData={trendChartRows}
              />
            ) : (
              <>
                <Stack
                  direction="row"
                  alignItems="center"
                  flexWrap="wrap"
                  spacing={1}
                  sx={{ padding: "10px", pb: 0, mb: 2 }}
                >
                  <Typography
                    sx={{
                      fontFamily: "Roboto, sans-serif",
                      fontWeight: 600,
                      fontSize: "16px",
                      color: "#212121",
                    }}
                  >
                    AI Alerts
                  </Typography>
                  {useStreamForAlertsTable &&
                    sseEnabled &&
                    connectionState === "open" && (
                      <Chip
                        size="small"
                        label="Live stream"
                        sx={{
                          height: 22,
                          fontSize: "11px",
                          fontWeight: 600,
                          backgroundColor: "#E8F5E9",
                          color: "#2E7D32",
                        }}
                      />
                    )}
                  {useStreamForAlertsTable &&
                    sseEnabled &&
                    connectionState === "connecting" && (
                      <Chip
                        size="small"
                        label="Connecting…"
                        sx={{
                          height: 22,
                          fontSize: "11px",
                          backgroundColor: "#E3F2FD",
                          color: "#1565C0",
                        }}
                      />
                    )}
                  {useStreamForAlertsTable &&
                    sseEnabled &&
                    connectionState === "error" && (
                      <Chip
                        size="small"
                        label={
                          lastError?.message
                            ? `Stream error: ${lastError.message.slice(0, 48)}`
                            : "Stream disconnected"
                        }
                        sx={{
                          height: 22,
                          fontSize: "11px",
                          maxWidth: 280,
                          backgroundColor: "#FFF3E0",
                          color: "#E65100",
                        }}
                      />
                    )}
                  {useStreamForAlertsTable && todayBaselineLoading && (
                    <Chip
                      size="small"
                      label="Loading today's baseline…"
                      sx={{
                        height: 22,
                        fontSize: "11px",
                        backgroundColor: "#E3F2FD",
                        color: "#1565C0",
                      }}
                    />
                  )}
                  {useStreamForAlertsTable &&
                    !todayBaselineLoading &&
                    todayBaselineError && (
                      <Chip
                        size="small"
                        label="Baseline error"
                        sx={{
                          height: 22,
                          fontSize: "11px",
                          backgroundColor: "#FFEBEE",
                          color: "#C62828",
                        }}
                      />
                    )}
                  {!useStreamForAlertsTable && historyLoading && (
                    <Chip
                      size="small"
                      label="Loading history…"
                      sx={{
                        height: 22,
                        fontSize: "11px",
                        backgroundColor: "#E3F2FD",
                        color: "#1565C0",
                      }}
                    />
                  )}
                  {!useStreamForAlertsTable &&
                    !historyLoading &&
                    !historyError && (
                      <Chip
                        size="small"
                        label="Alert history"
                        sx={{
                          height: 22,
                          fontSize: "11px",
                          fontWeight: 600,
                          backgroundColor: "#EDE7F6",
                          color: "#4527A0",
                        }}
                      />
                    )}
                  {!useStreamForAlertsTable && historyError && (
                    <Chip
                      size="small"
                      label="History error"
                      sx={{
                        height: 22,
                        fontSize: "11px",
                        backgroundColor: "#FFEBEE",
                        color: "#C62828",
                      }}
                    />
                  )}
                </Stack>

                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  flexWrap="wrap"
                  useFlexGap
                  sx={{ gap: 1, mb: 2, paddingX: "8px" }}
                >
                  <TextField
                    size="small"
                    placeholder="Search ID, Alert Name, Source, Zone, Notified To and Notified By"
                    title="Search by ID, Alert Name, Source, Zone, Notified To and Notified By"
                    value={tableSearch}
                    onChange={(e) => {
                      setPage(1);
                      setTableSearch(e.target.value);
                    }}
                    autoComplete="off"
                    name="ai-alerts-dashboard-search"
                    helperText={
                      tableSearch.trim().length === 1
                        ? "Enter at least 2 characters to search."
                        : undefined
                    }
                    FormHelperTextProps={{
                      sx: { marginLeft: "14px" },
                    }}
                    inputProps={{ "aria-label": "Global alert search" }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: "#9E9E9E", fontSize: 20 }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <MuiTooltip
                            title={DASHBOARD_ALERTS_SEARCH_SCOPE_TOOLTIP}
                            placement="top"
                            arrow
                          >
                            <IconButton
                              size="small"
                              edge="end"
                              aria-label="Search scope and rules"
                              tabIndex={-1}
                            >
                              <InfoOutlinedIcon
                                sx={{ fontSize: 18, color: "#9E9E9E" }}
                              />
                            </IconButton>
                          </MuiTooltip>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      flex: "1 1 260px",
                      minWidth: 260,
                      maxWidth: 560,
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "24px",
                        backgroundColor: "#FAFAFA",
                        fontFamily: "Roboto, sans-serif",
                        fontSize: "14px",
                      },
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => setViewMode("list")}
                    sx={{
                      borderRadius: "8px",
                      color: viewMode === "list" ? "#2932E5" : "#757575",
                      backgroundColor:
                        viewMode === "list"
                          ? "rgba(41, 50, 229, 0.12)"
                          : "transparent",
                    }}
                    aria-label="List view"
                  >
                    <ViewListIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => setViewMode("grid")}
                    sx={{
                      borderRadius: "8px",
                      color: viewMode === "grid" ? "#2932E5" : "#757575",
                      backgroundColor:
                        viewMode === "grid"
                          ? "rgba(41, 50, 229, 0.12)"
                          : "transparent",
                    }}
                    aria-label="Grid view"
                  >
                    <ViewModuleIcon />
                  </IconButton>

                  <FormControl size="small" sx={{ minWidth: 168 }}>
                    <Select
                      value={aiAlertsDateRange}
                      onChange={handleAiAlertsDateRangeChange}
                      displayEmpty
                      renderValue={() => aiAlertsDateSelectDisplay}
                      sx={{
                        borderRadius: "8px",
                        backgroundColor: "white",
                        fontSize: "13px",
                        fontFamily: "Roboto, sans-serif",
                      }}
                    >
                      {AI_ALERTS_DATE_PRESETS.map((opt) => (
                        <MenuItem
                          key={opt.value}
                          value={opt.value}
                          onClick={() => {
                            if (opt.value === "custom") {
                              if (aiAlertsDateRange !== "custom") {
                                prevAiDateRangeBeforeCustomRef.current =
                                  aiAlertsDateRange;
                              }
                              setAiCustomDialogOpen(true);
                            }
                          }}
                          sx={{ fontFamily: "Roboto, sans-serif" }}
                        >
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={(e) => setSourceFilterAnchor(e.currentTarget)}
                      endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 18 }} />}
                      aria-haspopup="listbox"
                      aria-expanded={Boolean(sourceFilterAnchor)}
                      aria-label="Sub-source"
                      sx={{
                        textTransform: "none",
                        borderRadius: "8px",
                        backgroundColor: "white",
                        fontSize: "13px",
                        fontFamily: "Roboto, sans-serif",
                        minWidth: 130,
                        height: 40,
                        border: sourceFilterAnchor
                          ? "1px solid #2932E5"
                          : "1px solid #e0e0e0",
                        color: "rgba(0, 0, 0, 0.87)",
                        justifyContent: "space-between",
                        pl: 1.5,
                        pr: 1,
                        "& .MuiButton-endIcon": { ml: 0.5, mr: 0 },
                        "& .MuiButton-label": {
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          flex: 1,
                          justifyContent: "flex-start",
                        },
                      }}
                    >
                      {getSourceFilterToolbarDisplay()}
                    </Button>
                    <Popover
                      open={Boolean(sourceFilterAnchor)}
                      anchorEl={sourceFilterAnchor}
                      onClose={() => setSourceFilterAnchor(null)}
                      anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "left",
                      }}
                      transformOrigin={{ vertical: "top", horizontal: "left" }}
                      slotProps={{
                        paper: {
                          sx: {
                            mt: 0.5,
                            maxHeight: 320,
                            minWidth: 240,
                            borderRadius: "8px",
                            boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
                          },
                        },
                      }}
                    >
                      <Box sx={{ py: 0.5 }}>
                        <MenuItem
                          dense
                          onClick={() => {
                            if (isSourceAllChecked) {
                              setIsSourceAllChecked(false);
                              setFilterSource([]);
                            } else {
                              setIsSourceAllChecked(true);
                              setFilterSource([]);
                            }
                          }}
                          sx={{
                            fontSize: "13px",
                            fontFamily: "Roboto, sans-serif",
                            py: 0.5,
                            color: "#2932E5",
                          }}
                        >
                          {isSourceAllChecked ? "All clear" : "All check"}
                        </MenuItem>
                        {dashboardCamerasLoading ? (
                          <MenuItem
                            disabled
                            dense
                            sx={{
                              fontSize: "13px",
                              fontFamily: "Roboto, sans-serif",
                            }}
                          >
                            Loading cameras…
                          </MenuItem>
                        ) : null}
                        {!dashboardCamerasLoading && dashboardCamerasError ? (
                          <MenuItem
                            disabled
                            dense
                            sx={{
                              fontSize: "13px",
                              fontFamily: "Roboto, sans-serif",
                            }}
                          >
                            Could not load cameras
                          </MenuItem>
                        ) : null}
                        {!dashboardCamerasLoading &&
                        !dashboardCamerasError &&
                        sources.filter(Boolean).length === 0 ? (
                          <MenuItem
                            disabled
                            dense
                            sx={{
                              fontSize: "13px",
                              fontFamily: "Roboto, sans-serif",
                            }}
                          >
                            No cameras available
                          </MenuItem>
                        ) : null}
                        {!dashboardCamerasLoading &&
                        !dashboardCamerasError &&
                        sources.filter(Boolean).length > 0 ? (
                          sources.filter(Boolean).map((s) => (
                            <MenuItem
                              key={s}
                              dense
                              onClick={() => {
                                const optionValues = sources.filter(Boolean);
                                if (isSourceAllChecked) {
                                  setIsSourceAllChecked(false);
                                  setFilterSource(optionValues.filter((x) => x !== s));
                                  return;
                                }
                                const next = filterSource.includes(s)
                                  ? filterSource.filter((x) => x !== s)
                                  : [...filterSource, s];
                                if (next.length === optionValues.length) {
                                  setIsSourceAllChecked(true);
                                  setFilterSource([]);
                                  return;
                                }
                                setIsSourceAllChecked(false);
                                setFilterSource(next);
                              }}
                              sx={{
                                fontSize: "13px",
                                fontFamily: "Roboto, sans-serif",
                                py: 0.5,
                              }}
                            >
                              <Checkbox
                                checked={
                                  isSourceAllChecked ||
                                  filterSource.includes(s)
                                }
                                size="small"
                                tabIndex={-1}
                                disableRipple
                                sx={{ p: 0.5, mr: 0.5 }}
                              />
                              {s}
                            </MenuItem>
                          ))
                        ) : null}
                      </Box>
                    </Popover>
                  </>

                  <>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={(e) => setZoneFilterAnchor(e.currentTarget)}
                      endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 18 }} />}
                      aria-haspopup="listbox"
                      aria-expanded={Boolean(zoneFilterAnchor)}
                      aria-label="Zone"
                      sx={{
                        textTransform: "none",
                        borderRadius: "8px",
                        backgroundColor: "white",
                        fontSize: "13px",
                        fontFamily: "Roboto, sans-serif",
                        minWidth: 100,
                        height: 40,
                        border: zoneFilterAnchor
                          ? "1px solid #2932E5"
                          : "1px solid #e0e0e0",
                        color: "rgba(0, 0, 0, 0.87)",
                        justifyContent: "space-between",
                        pl: 1.5,
                        pr: 1,
                        "& .MuiButton-endIcon": { ml: 0.5, mr: 0 },
                        "& .MuiButton-label": {
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          flex: 1,
                          justifyContent: "flex-start",
                        },
                      }}
                    >
                      {getZoneFilterToolbarDisplay()}
                    </Button>
                    <Popover
                      open={Boolean(zoneFilterAnchor)}
                      anchorEl={zoneFilterAnchor}
                      onClose={() => setZoneFilterAnchor(null)}
                      anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "left",
                      }}
                      transformOrigin={{ vertical: "top", horizontal: "left" }}
                      slotProps={{
                        paper: {
                          sx: {
                            mt: 0.5,
                            maxHeight: 320,
                            minWidth: 240,
                            borderRadius: "8px",
                            boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
                          },
                        },
                      }}
                    >
                      <Box sx={{ py: 0.5 }}>
                        <MenuItem
                          dense
                          onClick={() => {
                            if (isZoneAllChecked) {
                              setIsZoneAllChecked(false);
                              setFilterZone([]);
                            } else {
                              setIsZoneAllChecked(true);
                              setFilterZone([]);
                            }
                          }}
                          sx={{
                            fontSize: "13px",
                            fontFamily: "Roboto, sans-serif",
                            py: 0.5,
                            color: "#2932E5",
                          }}
                        >
                          {isZoneAllChecked ? "All clear" : "All check"}
                        </MenuItem>
                        {dashboardZonesLoading ? (
                          <MenuItem
                            disabled
                            dense
                            sx={{
                              fontSize: "13px",
                              fontFamily: "Roboto, sans-serif",
                            }}
                          >
                            Loading zones…
                          </MenuItem>
                        ) : null}
                        {!dashboardZonesLoading && dashboardZonesError ? (
                          <MenuItem
                            disabled
                            dense
                            sx={{
                              fontSize: "13px",
                              fontFamily: "Roboto, sans-serif",
                            }}
                          >
                            Could not load zones
                          </MenuItem>
                        ) : null}
                        {!dashboardZonesLoading &&
                        !dashboardZonesError &&
                        zoneFilterOptions.length === 0 ? (
                          <MenuItem
                            disabled
                            dense
                            sx={{
                              fontSize: "13px",
                              fontFamily: "Roboto, sans-serif",
                            }}
                          >
                            No zones available
                          </MenuItem>
                        ) : null}
                        {!dashboardZonesLoading &&
                        !dashboardZonesError &&
                        zoneFilterOptions.length > 0 ? (
                          zoneFilterOptions.map((z) => (
                            <MenuItem
                              key={z.value}
                              dense
                              onClick={() => {
                                const optionValues = zoneFilterOptions.map(
                                  (x) => x.value,
                                );
                                if (isZoneAllChecked) {
                                  setIsZoneAllChecked(false);
                                  setFilterZone(
                                    optionValues.filter((x) => x !== z.value),
                                  );
                                  return;
                                }
                                const next = filterZone.includes(z.value)
                                  ? filterZone.filter((x) => x !== z.value)
                                  : [...filterZone, z.value];
                                if (next.length === optionValues.length) {
                                  setIsZoneAllChecked(true);
                                  setFilterZone([]);
                                  return;
                                }
                                setIsZoneAllChecked(false);
                                setFilterZone(next);
                              }}
                              sx={{
                                fontSize: "13px",
                                fontFamily: "Roboto, sans-serif",
                                py: 0.5,
                              }}
                            >
                              <Checkbox
                                checked={
                                  isZoneAllChecked ||
                                  filterZone.includes(z.value)
                                }
                                size="small"
                                tabIndex={-1}
                                disableRipple
                                sx={{ p: 0.5, mr: 0.5 }}
                              />
                              {z.label}
                            </MenuItem>
                          ))
                        ) : null}
                      </Box>
                    </Popover>
                  </>

                  <>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={(e) => setAlertTypeFilterAnchor(e.currentTarget)}
                      endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 18 }} />}
                      aria-haspopup="listbox"
                      aria-expanded={Boolean(alertTypeFilterAnchor)}
                      aria-label="Alert Type"
                      sx={{
                        textTransform: "none",
                        borderRadius: "8px",
                        backgroundColor: "white",
                        fontSize: "13px",
                        fontFamily: "Roboto, sans-serif",
                        minWidth: 120,
                        height: 40,
                        border: alertTypeFilterAnchor
                          ? "1px solid #2932E5"
                          : "1px solid #e0e0e0",
                        color: "rgba(0, 0, 0, 0.87)",
                        justifyContent: "space-between",
                        pl: 1.5,
                        pr: 1,
                        "& .MuiButton-endIcon": { ml: 0.5, mr: 0 },
                        "& .MuiButton-label": {
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          flex: 1,
                          justifyContent: "flex-start",
                        },
                      }}
                    >
                      {getAiDashboardAlertTypeToolbarDisplay()}
                    </Button>
                    <Popover
                      open={Boolean(alertTypeFilterAnchor)}
                      anchorEl={alertTypeFilterAnchor}
                      onClose={() => setAlertTypeFilterAnchor(null)}
                      anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "left",
                      }}
                      transformOrigin={{ vertical: "top", horizontal: "left" }}
                      slotProps={{
                        paper: {
                          sx: {
                            mt: 0.5,
                            maxHeight: 320,
                            minWidth: 240,
                            borderRadius: "8px",
                            boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
                          },
                        },
                      }}
                    >
                      <Box sx={{ py: 0.5 }}>
                        <MenuItem
                          dense
                          onClick={() => {
                            if (isAlertTypeAllChecked) {
                              setIsAlertTypeAllChecked(false);
                              setFilterAlertType([]);
                            } else {
                              setIsAlertTypeAllChecked(true);
                              setFilterAlertType([]);
                            }
                          }}
                          sx={{
                            fontSize: "13px",
                            fontFamily: "Roboto, sans-serif",
                            py: 0.5,
                            color: "#2932E5",
                          }}
                        >
                          {isAlertTypeAllChecked ? "All clear" : "All check"}
                        </MenuItem>
                        {useCasesLoading ? (
                          <MenuItem
                            disabled
                            dense
                            sx={{ fontSize: "13px", fontFamily: "Roboto, sans-serif" }}
                          >
                            Loading use cases…
                          </MenuItem>
                        ) : null}
                        {!useCasesLoading && useCasesError ? (
                          <MenuItem
                            disabled
                            dense
                            sx={{ fontSize: "13px", fontFamily: "Roboto, sans-serif" }}
                          >
                            Could not load use cases
                          </MenuItem>
                        ) : null}
                        {!useCasesLoading &&
                          !useCasesError &&
                          alertTypeFilterOptions.map((o) => (
                            <MenuItem
                              key={o.value}
                              dense
                              onClick={() => {
                                const optionValues = alertTypeFilterOptions.map(
                                  (x) => x.value,
                                );
                                if (isAlertTypeAllChecked) {
                                  setIsAlertTypeAllChecked(false);
                                  setFilterAlertType(
                                    optionValues.filter((x) => x !== o.value),
                                  );
                                  return;
                                }
                                const next = filterAlertType.includes(o.value)
                                  ? filterAlertType.filter((x) => x !== o.value)
                                  : [...filterAlertType, o.value];
                                if (next.length === optionValues.length) {
                                  setIsAlertTypeAllChecked(true);
                                  setFilterAlertType([]);
                                  return;
                                }
                                setIsAlertTypeAllChecked(false);
                                setFilterAlertType(next);
                              }}
                              sx={{
                                fontSize: "13px",
                                fontFamily: "Roboto, sans-serif",
                                py: 0.5,
                              }}
                            >
                              <Checkbox
                                checked={
                                  isAlertTypeAllChecked ||
                                  filterAlertType.includes(o.value)
                                }
                                size="small"
                                tabIndex={-1}
                                disableRipple
                                sx={{ p: 0.5, mr: 0.5 }}
                              />
                              {o.label}
                            </MenuItem>
                          ))}
                      </Box>
                    </Popover>
                  </>

                  <IconButton
                    size="small"
                    onClick={(e) => setExtraFilterAnchor(e.currentTarget)}
                    sx={{
                      ml: "auto",
                      borderRadius: "8px",
                      border:
                        hasExtraFilters || Boolean(extraFilterAnchor)
                          ? "1px solid #2932E5"
                          : "1px solid #E0E0E0",
                      px: 1.5,
                      gap: 0.5,
                      fontFamily: "Roboto, sans-serif",
                      fontSize: "13px",
                      backgroundColor:
                        hasExtraFilters || Boolean(extraFilterAnchor)
                          ? "rgba(41, 50, 229, 0.06)"
                          : "transparent",
                      color:
                        hasExtraFilters || Boolean(extraFilterAnchor)
                          ? "#2932E5"
                          : "inherit",
                      "&:hover": {
                        backgroundColor:
                          hasExtraFilters || Boolean(extraFilterAnchor)
                            ? "rgba(41, 50, 229, 0.1)"
                            : "rgba(0, 0, 0, 0.04)",
                      },
                    }}
                    aria-label="Filter by severity and status"
                    aria-expanded={Boolean(extraFilterAnchor)}
                  >
                    <FilterListIcon sx={{ fontSize: 18 }} />
                    <Typography component="span" sx={{ fontSize: "13px" }}>
                      Filter
                    </Typography>
                  </IconButton>
                  <ResetFiltersButton
                    disabled={!hasActiveFilters}
                    onClick={handleResetFilters}
                  />
                  <Popover
                    open={Boolean(extraFilterAnchor)}
                    anchorEl={extraFilterAnchor}
                    onClose={() => setExtraFilterAnchor(null)}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                    slotProps={{
                      paper: {
                        sx: {
                          mt: 1,
                          borderRadius: "12px",
                          boxShadow: "0px 4px 24px rgba(0,0,0,0.12)",
                          border: "1px solid #E8E8E8",
                        },
                      },
                    }}
                  >
                    <Box sx={{ p: 2, minWidth: 280 }}>
                      <Typography
                        sx={{
                          fontFamily: "Roboto, sans-serif",
                          fontWeight: 600,
                          fontSize: "14px",
                          color: "#212121",
                          mb: 1.5,
                        }}
                      >
                        Severity &amp; status
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: "Roboto, sans-serif",
                          fontSize: "12px",
                          color: "#616161",
                          mb: 0.5,
                        }}
                      >
                        Severity
                      </Typography>
                      <Box
                        sx={{
                          maxHeight: 200,
                          overflowY: "auto",
                          mb: 2,
                          borderRadius: "8px",
                          border: "1px solid #EEEEEE",
                          backgroundColor: "#FAFAFA",
                        }}
                      >
                        <MenuItem
                          dense
                          onClick={() => {
                            if (isSeverityAllChecked) {
                              setIsSeverityAllChecked(false);
                              setFilterSeverity([]);
                            } else {
                              setIsSeverityAllChecked(true);
                              setFilterSeverity([]);
                            }
                          }}
                          sx={{
                            fontFamily: "Roboto, sans-serif",
                            fontSize: "14px",
                            py: 0.5,
                          }}
                        >
                          <Checkbox
                            checked={isSeverityAllChecked}
                            size="small"
                            tabIndex={-1}
                            disableRipple
                            sx={{ p: 0.5, mr: 0.5 }}
                          />
                          {isSeverityAllChecked ? "All clear" : "All check"}
                        </MenuItem>
                        {severityOptions.map((s) => (
                          <MenuItem
                            key={String(s)}
                            dense
                            onClick={() => {
                              if (isSeverityAllChecked) {
                                setIsSeverityAllChecked(false);
                                setFilterSeverity(
                                  severityOptions.filter((x) => x !== s),
                                );
                                return;
                              }
                              const next = filterSeverity.includes(s)
                                ? filterSeverity.filter((x) => x !== s)
                                : [...filterSeverity, s];
                              if (next.length === severityOptions.length) {
                                setIsSeverityAllChecked(true);
                                setFilterSeverity([]);
                                return;
                              }
                              setIsSeverityAllChecked(false);
                              setFilterSeverity(next);
                            }}
                            sx={{
                              fontFamily: "Roboto, sans-serif",
                              fontSize: "14px",
                              py: 0.5,
                            }}
                          >
                            <Checkbox
                              checked={
                                isSeverityAllChecked ||
                                filterSeverity.includes(s)
                              }
                              size="small"
                              tabIndex={-1}
                              disableRipple
                              sx={{ p: 0.5, mr: 0.5 }}
                            />
                            {s}
                          </MenuItem>
                        ))}
                      </Box>
                      <Typography
                        sx={{
                          fontFamily: "Roboto, sans-serif",
                          fontSize: "12px",
                          color: "#616161",
                          mb: 0.5,
                        }}
                      >
                        Status
                      </Typography>
                      <Box
                        sx={{
                          maxHeight: 200,
                          overflowY: "auto",
                          mb: 2,
                          borderRadius: "8px",
                          border: "1px solid #EEEEEE",
                          backgroundColor: "#FAFAFA",
                        }}
                      >
                        <MenuItem
                          dense
                          onClick={() => {
                            if (isStatusAllChecked) {
                              setIsStatusAllChecked(false);
                              setFilterStatus([]);
                            } else {
                              setIsStatusAllChecked(true);
                              setFilterStatus([]);
                            }
                          }}
                          sx={{
                            fontFamily: "Roboto, sans-serif",
                            fontSize: "14px",
                            py: 0.5,
                          }}
                        >
                          <Checkbox
                            checked={isStatusAllChecked}
                            size="small"
                            tabIndex={-1}
                            disableRipple
                            sx={{ p: 0.5, mr: 0.5 }}
                          />
                          {isStatusAllChecked ? "All clear" : "All check"}
                        </MenuItem>
                        {statusOptions.map((st) => (
                          <MenuItem
                            key={String(st)}
                            dense
                            onClick={() => {
                              if (isStatusAllChecked) {
                                setIsStatusAllChecked(false);
                                setFilterStatus(
                                  statusOptions.filter((x) => x !== st),
                                );
                                return;
                              }
                              const next = filterStatus.includes(st)
                                ? filterStatus.filter((x) => x !== st)
                                : [...filterStatus, st];
                              if (next.length === statusOptions.length) {
                                setIsStatusAllChecked(true);
                                setFilterStatus([]);
                                return;
                              }
                              setIsStatusAllChecked(false);
                              setFilterStatus(next);
                            }}
                            sx={{
                              fontFamily: "Roboto, sans-serif",
                              fontSize: "14px",
                              py: 0.5,
                            }}
                          >
                            <Checkbox
                              checked={
                                isStatusAllChecked || filterStatus.includes(st)
                              }
                              size="small"
                              tabIndex={-1}
                              disableRipple
                              sx={{ p: 0.5, mr: 0.5 }}
                            />
                            {st}
                          </MenuItem>
                        ))}
                      </Box>

                      <Typography
                        sx={{
                          fontFamily: "Roboto, sans-serif",
                          fontSize: "12px",
                          color: "#616161",
                          mb: 0.5,
                        }}
                      >
                        Notification status
                      </Typography>
                      <Box
                        sx={{
                          maxHeight: 200,
                          overflowY: "auto",
                          mb: 2,
                          borderRadius: "8px",
                          border: "1px solid #EEEEEE",
                          backgroundColor: "#FAFAFA",
                        }}
                      >
                        <MenuItem
                          dense
                          onClick={() => {
                            if (isNotificationStatusAllChecked) {
                              setIsNotificationStatusAllChecked(false);
                              setFilterNotificationStatus([]);
                            } else {
                              setIsNotificationStatusAllChecked(true);
                              setFilterNotificationStatus([]);
                            }
                          }}
                          sx={{
                            fontFamily: "Roboto, sans-serif",
                            fontSize: "14px",
                            py: 0.5,
                          }}
                        >
                          <Checkbox
                            checked={isNotificationStatusAllChecked}
                            size="small"
                            tabIndex={-1}
                            disableRipple
                            sx={{ p: 0.5, mr: 0.5 }}
                          />
                          {isNotificationStatusAllChecked
                            ? "All clear"
                            : "All check"}
                        </MenuItem>
                        {NOTIFICATION_STATUS_FILTER_OPTIONS.map(
                          ({ value, label }) => (
                            <MenuItem
                              key={value}
                              dense
                              onClick={() => {
                                const optionValues =
                                  NOTIFICATION_STATUS_FILTER_OPTIONS.map(
                                    (x) => x.value,
                                  );
                                if (isNotificationStatusAllChecked) {
                                  setIsNotificationStatusAllChecked(false);
                                  setFilterNotificationStatus(
                                    optionValues.filter((x) => x !== value),
                                  );
                                  return;
                                }
                                const next = filterNotificationStatus.includes(
                                  value,
                                )
                                  ? filterNotificationStatus.filter(
                                      (x) => x !== value,
                                    )
                                  : [...filterNotificationStatus, value];
                                if (next.length === optionValues.length) {
                                  setIsNotificationStatusAllChecked(true);
                                  setFilterNotificationStatus([]);
                                  return;
                                }
                                setIsNotificationStatusAllChecked(false);
                                setFilterNotificationStatus(next);
                              }}
                              sx={{
                                fontFamily: "Roboto, sans-serif",
                                fontSize: "14px",
                                py: 0.5,
                              }}
                            >
                              <Checkbox
                                checked={filterNotificationStatus.includes(
                                  value,
                                ) || isNotificationStatusAllChecked}
                                size="small"
                                tabIndex={-1}
                                disableRipple
                                sx={{ p: 0.5, mr: 0.5 }}
                              />
                              {label}
                            </MenuItem>
                          ),
                        )}
                      </Box>
                    </Box>
                  </Popover>
                </Stack>

                {viewMode === "list" ? (
                  <>
                    <Box
                      sx={{
                        overflow: "hidden",
                        backgroundColor: "#fff",
                      }}
                    >
                      <TableContainer
                        component={Paper}
                        elevation={0}
                        sx={{
                          borderRadius: 0,
                          border: "none",
                          boxShadow: "none",
                          maxHeight: AI_ALERTS_CONTENT_MIN_HEIGHT,
                          minHeight: AI_ALERTS_CONTENT_MIN_HEIGHT,
                          overflowY: "auto",
                          overflowX: "auto",
                        }}
                      >
                        <Table
                          stickyHeader
                          size="small"
                          sx={{ minWidth: 1000 }}
                        >
                          <TableHead>
                            <TableRow
                              sx={{
                                backgroundColor: "#B3E5FC",
                                "& th": {
                                  fontFamily: "Roboto, sans-serif",
                                  fontWeight: 600,
                                  fontSize: "13px",
                                  color: "#01579B",
                                  borderBottom: "none",
                                  py: 1.25,
                                  backgroundColor: "#B3E5FC",
                                },
                              }}
                            >
                              <TableCell
                                sx={{
                                  fontWeight: 600,
                                  maxWidth: 120,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                ID
                              </TableCell>
                              <TableCell>Alert Name</TableCell>
                              <TableCell>Severity</TableCell>
                              <TableCell>Date &amp; Time</TableCell>
                              <TableCell>Alert Type</TableCell>
                              <TableCell>Source</TableCell>
                              <TableCell>Zone</TableCell>
                              <TableCell>Status</TableCell>
                              <TableCell>Notified To</TableCell>
                              <TableCell>Notified By</TableCell>
                              <TableCell align="right">Action</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {pagedRows.length === 0 ? (
                              <TableRow>
                                <TableCell
                                  colSpan={11}
                                  align="center"
                                  sx={{
                                    py: 6,
                                    fontFamily: "Roboto, sans-serif",
                                    fontSize: "15px",
                                    color: "#757575",
                                    border: "none",
                                  }}
                                >
                                  {aiAlertsEmptyMessage}
                                </TableCell>
                              </TableRow>
                            ) : (
                              pagedRows.map((row) => {
                                const rowStatus = getEffectiveDashboardRowStatus(
                                  row,
                                  localStatusOverrides,
                                );
                                return (
                                <TableRow
                                  key={row.rowKey}
                                  hover
                                  onClick={() => setSourceDetailRow(row)}
                                  onContextMenu={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setActionAnchor(null);
                                    setActionMenuPointerPosition({
                                      top: e.clientY,
                                      left: e.clientX,
                                    });
                                    setActionMenuRowKey(row.rowKey);
                                  }}
                                  sx={{
                                    cursor: "pointer",
                                    "& td": {
                                      fontFamily: "Roboto, sans-serif",
                                      fontSize: "13px",
                                      borderColor: "#EEEEEE",
                                    },
                                  }}
                                >
                                  <TableCell
                                    sx={{
                                      fontSize: "12px",
                                      color: "#4A5565",
                                      maxWidth: 120,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}
                                    title={
                                      row.srId != null &&
                                      String(row.srId).trim() !== ""
                                        ? String(row.srId)
                                        : undefined
                                    }
                                  >
                                    {row.srId != null &&
                                    String(row.srId).trim() !== ""
                                      ? String(row.srId).trim()
                                      : "—"}
                                  </TableCell>
                                  <TableCell>{row.alertName}</TableCell>
                                  <TableCell>{row.severity}</TableCell>
                                  <TableCell>
                                    <Chip
                                      label={formatDateTimePill(row.at)}
                                      size="small"
                                      sx={{
                                        backgroundColor: "#FFE0B2",
                                        color: "#E65100",
                                        fontWeight: 500,
                                        fontSize: "11px",
                                        height: "26px",
                                      }}
                                    />
                                  </TableCell>
                                  <TableCell>{row.alertType}</TableCell>
                                  <TableCell
                                    sx={{
                                      // Keep source cell visually consistent with the row hover.
                                    }}
                                  >
                                    <Stack
                                      direction="row"
                                      alignItems="center"
                                      spacing={1}
                                    >
                                      <VideocamIcon
                                        sx={{ fontSize: 18, color: "#424242" }}
                                      />
                                      <Typography
                                        sx={{
                                          fontSize: "13px",
                                          color: "#2932E5",
                                          fontWeight: 500,
                                          textDecoration: "underline",
                                          textDecorationColor:
                                            "rgba(41, 50, 229, 0.35)",
                                        }}
                                      >
                                        {row.sourceName}
                                      </Typography>
                                      <Box
                                        component="img"
                                        src={row.thumb}
                                        alt=""
                                        sx={{
                                          width: 36,
                                          height: 28,
                                          objectFit: "cover",
                                          borderRadius: "4px",
                                          border: "1px solid #E0E0E0",
                                        }}
                                      />
                                    </Stack>
                                  </TableCell>
                                  <TableCell>{row.zone}</TableCell>
                                  <TableCell>
                                    <Chip
                                      label={rowStatus}
                                      size="small"
                                      sx={{
                                        backgroundColor:
                                          STATUS_STYLES[rowStatus]?.bg,
                                        color: STATUS_STYLES[rowStatus]?.color,
                                        fontWeight: 600,
                                        fontSize: "11px",
                                        height: "24px",
                                      }}
                                    />
                                  </TableCell>
                                  <TableCell sx={{ maxWidth: 160 }}>
                                    <Typography
                                      noWrap
                                      title={row.notifiedTo}
                                      sx={{ fontSize: "12px" }}
                                    >
                                      {row.notifiedTo}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <AlertNotifiedByChips
                                      row={row}
                                      channels={dashboardRowNotifyChipChannels(
                                        row,
                                      )}
                                      notificationStatus={row.notificationStatus}
                                    />
                                  </TableCell>
                                  <TableCell align="right">
                                    <IconButton
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setActionMenuPointerPosition(null);
                                        setActionAnchor(e.currentTarget);
                                        setActionMenuRowKey(row.rowKey);
                                      }}
                                      aria-label="Row actions"
                                    >
                                      <MoreVertIcon fontSize="small" />
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                                );
                              })
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      <AlertServicesPaginationFooter
                        total={paginationTotal}
                        page={page}
                        rowsPerPage={rowsPerPage}
                        onPageChange={setPage}
                        onRowsPerPageChange={(n) => {
                          setRowsPerPage(n);
                          setPage(1);
                        }}
                      />
                    </Box>
                  </>
                ) : (
                  <>
                    <Box
                      sx={{
                        width: "100%",
                        overflow: "hidden",
                        backgroundColor: "#fff",
                      }}
                    >
                      <Box
                        sx={{
                          maxHeight: AI_ALERTS_CONTENT_MIN_HEIGHT,
                          minHeight: AI_ALERTS_CONTENT_MIN_HEIGHT,
                          overflow: "auto",
                          p: { xs: 0.5, sm: 0 },
                          pb: 1,
                          boxSizing: "border-box",
                        }}
                      >
                        <Grid container spacing={1}>
                          {pagedRows.length === 0 ? (
                            <Grid item xs={12}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  minHeight: AI_ALERTS_CONTENT_MIN_HEIGHT,
                                  fontFamily: "Roboto, sans-serif",
                                  fontSize: "15px",
                                  color: "#757575",
                                }}
                              >
                                {aiAlertsEmptyMessage}
                              </Box>
                            </Grid>
                          ) : (
                            pagedRows.map((row) => {
                              const hero = dashboardGridHeroMode(row);
                              const rk = row.rowKey;
                              const imgErr = Boolean(aiGridImageErrors[rk]);
                              const showVideoHero =
                                hero.kind === "video" &&
                                Boolean(hero.videoHref) &&
                                !imgErr;
                              const showPhotoHero =
                                hero.kind === "image" &&
                                Boolean(hero.photoHref) &&
                                !imgErr;
                              const rowStatus =
                                getEffectiveDashboardRowStatus(
                                  row,
                                  localStatusOverrides,
                                );
                              const notifyChips =
                                dashboardRowNotifyChipChannels(row);
                              return (
                                <Grid
                                  item
                                  xs={12}
                                  sm={6}
                                  md={4}
                                  lg={3}
                                  xl={2}
                                  key={row.rowKey}
                                  sx={{ display: "flex", minWidth: 0 }}
                                >
                                  <Card
                                    onClick={() => setSourceDetailRow(row)}
                                    onContextMenu={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      setActionAnchor(null);
                                      setActionMenuPointerPosition({
                                        top: e.clientY,
                                        left: e.clientX,
                                      });
                                      setActionMenuRowKey(row.rowKey);
                                    }}
                                    sx={{
                                      position: "relative",
                                      display: "flex",
                                      flexDirection: "column",
                                      width: "100%",
                                      minWidth: 0,
                                      height: "auto",
                                      minHeight: { xs: 200, sm: 230 },
                                      bgcolor: "rgb(255, 255, 255)",
                                      boxShadow: "var(--Paper-shadow)",
                                      overflow: "hidden",
                                      borderRadius: "14px",
                                      transition:
                                        "0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                                      cursor: "pointer",
                                      border: "1px solid rgb(229, 231, 235)",
                                      "&:hover": {
                                        boxShadow:
                                          "0 8px 16px rgba(0,0,0,0.12)",
                                        transform:
                                          "translateY(-4px) scale(1.02)",
                                      },
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        width: "100%",
                                        minWidth: 0,
                                        paddingLeft: { xs: 1, sm: "10px" },
                                        paddingRight: { xs: 1, sm: "10px" },
                                        pt: 1,
                                        pb: 0,
                                        flexShrink: 0,
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          width: "100%",
                                          maxWidth: "100%",
                                          height: { xs: "96px", sm: "112.5px" },
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          bgcolor: "rgb(33, 33, 33)",
                                          borderRadius: "8px",
                                          overflow: "hidden",
                                          position: "relative",
                                        }}
                                      >
                                        {showVideoHero ? (
                                          <AlertHistoryMediaVideo
                                            href={hero.videoHref}
                                            sx={{
                                              position: "absolute",
                                              inset: 0,
                                              width: "100%",
                                              height: "100%",
                                            }}
                                            videoSx={{
                                              width: "100%",
                                              height: "100%",
                                              objectFit: "cover",
                                              display: "block",
                                            }}
                                          />
                                        ) : showPhotoHero ? (
                                          <AlertHistoryMediaImage
                                            href={hero.photoHref}
                                            alt=""
                                            sx={{
                                              position: "absolute",
                                              inset: 0,
                                              width: "100%",
                                              height: "100%",
                                            }}
                                            imgSx={{
                                              width: "100%",
                                              height: "100%",
                                              objectFit: "cover",
                                              display: "block",
                                            }}
                                            onError={() => {
                                              setAiGridImageErrors((prev) => ({
                                                ...prev,
                                                [rk]: true,
                                              }));
                                            }}
                                          />
                                        ) : (
                                          <ImageIcon
                                            sx={{
                                              fontSize: 40,
                                              color: "rgba(255,255,255,0.35)",
                                            }}
                                            aria-hidden
                                          />
                                        )}
                                        <Box
                                          sx={{
                                            position: "absolute",
                                            top: 8,
                                            left: 8,
                                            zIndex: 2,
                                            color: "rgba(255,255,255,0.95)",
                                            display: "flex",
                                            alignItems: "center",
                                            filter:
                                              "drop-shadow(0 1px 2px rgba(0,0,0,0.6))",
                                          }}
                                        >
                                          {showVideoHero ? (
                                            <PlayArrowIcon sx={{ fontSize: 20 }} />
                                          ) : (
                                            <ImageIcon sx={{ fontSize: 20 }} />
                                          )}
                                        </Box>
                                        <IconButton
                                          size="small"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setActionMenuPointerPosition(null);
                                            setActionAnchor(e.currentTarget);
                                            setActionMenuRowKey(row.rowKey);
                                          }}
                                          sx={{
                                            position: "absolute",
                                            top: 8,
                                            right: 8,
                                            zIndex: 1,
                                            color: "rgba(255,255,255,0.95)",
                                            bgcolor: "rgba(255,255,255,0.15)",
                                            padding: "6px",
                                            "&:hover": {
                                              bgcolor: "rgba(255,255,255,0.3)",
                                              color: "#fff",
                                            },
                                          }}
                                          aria-label="Alert actions"
                                        >
                                          <MoreVertIcon sx={{ fontSize: 20 }} />
                                        </IconButton>
                                        <Box
                                          sx={{
                                            position: "absolute",
                                            bottom: 10,
                                            left: 10,
                                            zIndex: 1,
                                            maxWidth: "calc(100% - 20px)",
                                            px: 1.25,
                                            py: 0.5,
                                            borderRadius: "20px",
                                            bgcolor:
                                              "rgba(255, 179, 128, 0.92)",
                                            color: "#fff",
                                            fontFamily: "Roboto, sans-serif",
                                            fontSize: "10px",
                                            fontWeight: 600,
                                            letterSpacing: "0.02em",
                                            textTransform: "uppercase",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                          }}
                                          title={row.alertType}
                                        >
                                          {(row.alertType || "Alert")
                                            .replace(/\s+/g, " ")
                                            .toUpperCase()}
                                        </Box>
                                      </Box>
                                    </Box>
                                    <Box
                                      sx={{
                                        p: { xs: 1, sm: 1.5 },
                                        pt: { xs: 1, sm: 1.25 },
                                        flex: 1,
                                        minWidth: 0,
                                        minHeight: { xs: 72, sm: 88 },
                                        display: "flex",
                                        flexDirection: "column",
                                        justifyContent: "flex-start",
                                        overflow: "visible",
                                      }}
                                    >
                                      <Typography
                                        sx={{
                                          fontFamily: "Roboto, sans-serif",
                                          fontSize: { xs: "14px", sm: "16px" },
                                          fontWeight: 700,
                                          color: "#212121",
                                          mb: 0.5,
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap",
                                          minWidth: 0,
                                        }}
                                      >
                                        {row.alertName}
                                      </Typography>
                                      {row.srId != null &&
                                      String(row.srId).trim() !== "" ? (
                                        <Typography
                                          sx={{
                                            fontFamily: "Roboto, sans-serif",
                                            fontSize: {
                                              xs: "11px",
                                              sm: "12px",
                                            },
                                            color: "#6B7280",
                                            mb: 0.35,
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                            minWidth: 0,
                                          }}
                                        >
                                          ID: {String(row.srId).trim()}
                                        </Typography>
                                      ) : null}
                                      <Typography
                                        sx={{
                                          fontFamily: "Roboto, sans-serif",
                                          fontSize: { xs: "12px", sm: "13px" },
                                          color: "#4A5565",
                                          mb: 0.35,
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap",
                                          minWidth: 0,
                                        }}
                                      >
                                        {row.severity}
                                        {" · "}
                                        {row.zone}
                                      </Typography>
                                      <Chip
                                        label={formatDateTimePill(row.at)}
                                        size="small"
                                        sx={{
                                          alignSelf: "flex-start",
                                          maxWidth: "100%",
                                          backgroundColor: "#FFE0B2",
                                          color: "#E65100",
                                          fontWeight: 500,
                                          fontSize: "11px",
                                          height: "24px",
                                          mb: 0.35,
                                          "& .MuiChip-label": {
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                          },
                                        }}
                                      />
                                      <Stack
                                        direction="row"
                                        alignItems="center"
                                        spacing={0.5}
                                        sx={{ minWidth: 0, mb: 0.25 }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setSourceDetailRow(row);
                                        }}
                                      >
                                        <VideocamIcon
                                          sx={{
                                            fontSize: 14,
                                            color: "#424242",
                                            flexShrink: 0,
                                          }}
                                        />
                                        <Typography
                                          sx={{
                                            fontFamily: "Roboto, sans-serif",
                                            fontSize: "12px",
                                            color: "#2932E5",
                                            fontWeight: 500,
                                            textDecoration: "underline",
                                            textDecorationColor:
                                              "rgba(41, 50, 229, 0.35)",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                            minWidth: 0,
                                            cursor: "pointer",
                                          }}
                                        >
                                          {row.sourceName}
                                        </Typography>
                                      </Stack>
                                      <Stack
                                        direction="row"
                                        alignItems="center"
                                        spacing={0.5}
                                        flexWrap="wrap"
                                        useFlexGap
                                        sx={{
                                          gap: 0.5,
                                          mt: "auto",
                                          pt: 0.5,
                                          minWidth: 0,
                                        }}
                                      >
                                        <Chip
                                          label={rowStatus}
                                          size="small"
                                          sx={{
                                            height: "22px",
                                            fontSize: "11px",
                                            fontWeight: 600,
                                            backgroundColor:
                                              STATUS_STYLES[rowStatus]?.bg,
                                            color:
                                              STATUS_STYLES[rowStatus]?.color,
                                          }}
                                        />
                                        <AlertNotifiedByChips
                                          row={row}
                                          channels={notifyChips}
                                          notificationStatus={row.notificationStatus}
                                          stackSx={{ maxWidth: "100%" }}
                                        />
                                      </Stack>
                                    </Box>
                                  </Card>
                                </Grid>
                              );
                            })
                          )}
                        </Grid>
                      </Box>
                    </Box>
                    <AlertServicesPaginationFooter
                      total={paginationTotal}
                      page={page}
                      rowsPerPage={rowsPerPage}
                      onPageChange={setPage}
                      onRowsPerPageChange={(n) => {
                        setRowsPerPage(n);
                        setPage(1);
                      }}
                    />
                  </>
                )}

                <Menu
                  anchorReference={
                    actionMenuPointerPosition ? "anchorPosition" : "anchorEl"
                  }
                  anchorEl={actionAnchor}
                  anchorPosition={
                    actionMenuPointerPosition ?? undefined
                  }
                  open={Boolean(actionAnchor || actionMenuPointerPosition)}
                  onClose={closeAiAlertActionMenu}
                  anchorOrigin={
                    actionMenuPointerPosition
                      ? { vertical: "top", horizontal: "left" }
                      : { vertical: "bottom", horizontal: "right" }
                  }
                  transformOrigin={
                    actionMenuPointerPosition
                      ? { vertical: "top", horizontal: "left" }
                      : { vertical: "top", horizontal: "right" }
                  }
                >
                  <MenuItem
                    onClick={handleDashboardRowAcknowledge}
                    disabled={
                      !actionMenuRow ||
                      actionMenuEffectiveStatus !== "Active"
                    }
                  >
                    <ListItemIcon>
                      <AccessTimeIcon
                        fontSize="small"
                        sx={{ color: "#2E7D32" }}
                      />
                    </ListItemIcon>
                    Acknowledge
                  </MenuItem>
                  <MenuItem
                    onClick={handleDashboardRowClose}
                    disabled={
                      !actionMenuRow || actionMenuEffectiveStatus === "Closed"
                    }
                  >
                    <ListItemIcon>
                      <CheckCircleIcon
                        fontSize="small"
                        sx={{ color: "#7B1FA2" }}
                      />
                    </ListItemIcon>
                    Close
                  </MenuItem>
                </Menu>
              </>
            )}
          </CardContent>
        </Card>
      </Box>

      <AlertSourceDetailDialog
        open={Boolean(sourceDetailRow)}
        row={sourceDetailRow}
        localStatusOverrides={localStatusOverrides}
        onClose={() => setSourceDetailRow(null)}
        onAcknowledge={handleDetailDialogAcknowledge}
        onMarkClosed={handleDetailDialogMarkClosed}
      />

      <CustomDateRangeDialog
        open={aiCustomDialogOpen}
        startDate={aiCustomStart}
        endDate={aiCustomEnd}
        onCancel={handleAiCustomRangeCancel}
        onApply={handleAiCustomRangeApply}
      />
    </Box>
  );
}

const CRITICAL_BAR = "#E53935";

/** Stacked trend by day — align with Alert Overview KPI card colors. */
const TREND_COLOR_AI = "#2932E5";
const TREND_COLOR_IOT = "#FF9800";
const TREND_COLOR_SYSTEM = "#00C853";
const TREND_COLOR_CRITICAL = "#D32F2F";

/**
 * Min share of a day's bar for each non-zero segment (e.g. 14 non-critical vs 2472 critical).
 * Display stack is renormalized to the day's target height; API counts stay unchanged.
 */
const TREND_MIN_SEGMENT_FRACTION = 0.05;

function buildTrendStackDisplay(row, dayScale) {
  const targetTotal = (Number(row?.total) || 0) * dayScale;
  const parts = [
    { stackKey: "stackAiNc", value: row.aiNc },
    { stackKey: "stackIotNc", value: row.iotNc },
    { stackKey: "stackSystemNc", value: row.systemNc },
    { stackKey: "stackCritical", value: row.critical },
  ];

  const empty = {
    stackAiNc: null,
    stackIotNc: null,
    stackSystemNc: null,
    stackCritical: null,
  };

  const active = parts
    .map((p) => ({ ...p, value: Math.max(0, Number(p.value) || 0) }))
    .filter((p) => p.value > 0);

  if (active.length === 0 || targetTotal <= 0) return empty;

  const minEach = targetTotal * TREND_MIN_SEGMENT_FRACTION;
  if (minEach * active.length >= targetTotal) {
    const share = targetTotal / active.length;
    const out = { ...empty };
    for (const p of active) out[p.stackKey] = share;
    return out;
  }

  const segments = active.map((p) => {
    const raw = p.value * dayScale;
    return { ...p, raw, display: Math.max(raw, minEach) };
  });

  let sum = segments.reduce((acc, s) => acc + s.display, 0);
  if (sum > targetTotal) {
    let excess = sum - targetTotal;
    const sorted = [...segments].sort(
      (a, b) => b.display - minEach - (a.display - minEach),
    );
    for (const seg of sorted) {
      const reducible = seg.display - minEach;
      if (reducible <= 0) continue;
      const cut = Math.min(excess, reducible);
      seg.display -= cut;
      excess -= cut;
      if (excess <= 0) break;
    }
  }

  const out = { ...empty };
  for (const seg of segments) {
    out[seg.stackKey] = seg.display > 0 ? seg.display : null;
  }
  return out;
}

/** Trend chart X-axis / tooltip day label, e.g. "June 02", "July 21". */
function formatTrendChartDayLabel(raw) {
  if (raw == null || raw === "") return "—";
  const s = String(raw).trim().slice(0, 10);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return s || "—";
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (!month || !day) return "—";
  const dt = new Date(year, month - 1, day);
  if (Number.isNaN(dt.getTime())) return "—";
  const monthName = dt.toLocaleString("en-US", { month: "long" });
  return `${monthName} ${String(day).padStart(2, "0")}`;
}

function formatTrendAxisCount(n) {
  const v = Math.round(Number(n)) || 0;
  return v.toLocaleString("en-US");
}

/** Full AI count (matches overview KPI), drawn inside the purple stack segment. */
function TrendSegmentLabelAi(props) {
  const { x, y, width, height, payload } = props;
  if (
    payload == null ||
    x == null ||
    y == null ||
    width == null ||
    height == null
  ) {
    return null;
  }
  const ai = Number(payload.aiNc) || 0;
  const h = Number(height) || 0;
  if (ai <= 0 || h < 20) return null;
  const fs = Math.min(12, Math.max(10, Math.round(h / 8)));
  return (
    <text
      x={x + width / 2}
      y={y + h / 2}
      fill="rgba(255,255,255,0.96)"
      fontSize={fs}
      fontWeight={600}
      fontFamily="Roboto, sans-serif"
      textAnchor="middle"
      dominantBaseline="middle"
    >
      {formatTrendAxisCount(ai)}
    </text>
  );
}

function TrendSegmentLabelIot(props) {
  const { x, y, width, height, payload } = props;
  if (
    payload == null ||
    x == null ||
    y == null ||
    width == null ||
    height == null
  ) {
    return null;
  }
  const v = Number(payload.iotNc) || 0;
  const h = Number(height) || 0;
  if (v <= 0 || h < 18) return null;
  return (
    <text
      x={x + width / 2}
      y={y + h / 2}
      fill="rgba(255,255,255,0.95)"
      fontSize={11}
      fontWeight={600}
      fontFamily="Roboto, sans-serif"
      textAnchor="middle"
      dominantBaseline="middle"
    >
      {formatTrendAxisCount(v)}
    </text>
  );
}

function TrendSegmentLabelSystem(props) {
  const { x, y, width, height, payload } = props;
  if (
    payload == null ||
    x == null ||
    y == null ||
    width == null ||
    height == null
  ) {
    return null;
  }
  const v = Number(payload.systemNc) || 0;
  const h = Number(height) || 0;
  if (v <= 0 || h < 18) return null;
  return (
    <text
      x={x + width / 2}
      y={y + h / 2}
      fill="rgba(255,255,255,0.95)"
      fontSize={11}
      fontWeight={600}
      fontFamily="Roboto, sans-serif"
      textAnchor="middle"
      dominantBaseline="middle"
    >
      {formatTrendAxisCount(v)}
    </text>
  );
}

/** Critical total for the day, inside the red segment. */
function TrendSegmentLabelCritical(props) {
  const { x, y, width, height, payload } = props;
  if (
    payload == null ||
    x == null ||
    y == null ||
    width == null ||
    height == null
  ) {
    return null;
  }
  const c = Number(payload.critical) || 0;
  const h = Number(height) || 0;
  if (c <= 0 || h < 16) return null;
  const fs = Math.min(12, Math.max(10, Math.round(h / 6)));
  return (
    <text
      x={x + width / 2}
      y={y + h / 2}
      fill="rgba(255,255,255,0.96)"
      fontSize={fs}
      fontWeight={600}
      fontFamily="Roboto, sans-serif"
      textAnchor="middle"
      dominantBaseline="middle"
    >
      {formatTrendAxisCount(c)}
    </text>
  );
}

/**
 * Total count above each day — uses a minimum pixel stack height so labels stay
 * aligned with visible bars when counts are tiny relative to the Y-axis max.
 */
function TrendChartDayTotalLabels(props) {
  const { xAxisMap, yAxisMap, offset, data } = props;
  if (!Array.isArray(data) || data.length === 0) return null;
  const xAxis = xAxisMap ? Object.values(xAxisMap)[0] : null;
  const yAxis = yAxisMap ? Object.values(yAxisMap)[0] : null;
  if (!xAxis?.scale || !yAxis?.scale) return null;

  const { left = 0, top = 0 } = offset || {};
  const xScale = xAxis.scale;
  const yScale = yAxis.scale;
  const band = typeof xScale.bandwidth === "function" ? xScale.bandwidth() : 0;
  const baselineY =
    typeof yScale === "function" ? yScale(0) : top + (yAxis.height ?? 0);

  return (
    <g className="trend-day-total-labels">
      {data.map((row, i) => {
        const total = Number(row?.total) || 0;
        if (total <= 0) return null;
        const stackTop =
          Number(row?.stackTotalDisplay ?? row?.total) || total;
        const x0 =
          typeof xScale === "function" ? xScale(row?.name ?? "") : null;
        if (x0 == null || Number.isNaN(x0)) return null;
        const cx = left + x0 + band / 2;
        const barTopY =
          typeof yScale === "function" ? yScale(stackTop) : baselineY;
        return (
          <text
            key={row?.date ?? row?.name ?? i}
            x={cx}
            y={barTopY - 8}
            fill="#9E9E9E"
            fontSize={11}
            fontFamily="Roboto, sans-serif"
            textAnchor="middle"
          >
            {formatTrendAxisCount(total)}
          </text>
        );
      })}
    </g>
  );
}

function TrendChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const entry = payload[0];
  const p = entry?.payload;
  if (!p) return null;
  const dataKey = entry?.dataKey;
  const dateTitle =
    label != null && label !== "" ? String(label) : String(p.name ?? "—");

  let accent = "#64748B";
  let seriesLabel = "Alerts";
  let value = 0;
  if (dataKey === "stackAiNc") {
    accent = TREND_COLOR_AI;
    seriesLabel = "AI alerts";
    value = Number(p.ai) || 0;
  } else if (dataKey === "stackIotNc") {
    accent = TREND_COLOR_IOT;
    seriesLabel = "IoT alerts";
    value = Number(p.iot) || 0;
  } else if (dataKey === "stackSystemNc") {
    accent = TREND_COLOR_SYSTEM;
    seriesLabel = "System alerts";
    value = Number(p.system) || 0;
  } else if (dataKey === "stackCritical") {
    accent = TREND_COLOR_CRITICAL;
    seriesLabel = "Critical";
    value = Number(p.critical) || 0;
  } else {
    seriesLabel = String(entry?.name ?? "—");
    value = Number(entry?.value) || 0;
    accent = entry?.color || "#64748B";
  }

  if (value <= 0) return null;

  const valueStr = Number.isFinite(value)
    ? Math.round(value).toLocaleString("en-US")
    : "0";

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: "12px",
        border: "1px solid rgba(15, 23, 42, 0.08)",
        boxShadow: "0 12px 40px rgba(15, 23, 42, 0.14)",
        overflow: "hidden",
        minWidth: 188,
        maxWidth: 260,
        background:
          "linear-gradient(180deg, #ffffff 0%, #fafbfc 100%)",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "stretch", minHeight: 72 }}>
        <Box
          sx={{
            width: 5,
            flexShrink: 0,
            backgroundColor: accent,
            boxShadow: `inset 0 0 0 1px ${accent}`,
          }}
        />
        <Stack spacing={1.25} sx={{ px: 2, py: 1.75, flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontFamily: "Roboto, sans-serif",
              fontWeight: 700,
              fontSize: "17px",
              color: "#0f172a",
              lineHeight: 1.2,
            }}
          >
            {dateTitle}
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            <LegendDot color={accent} />
            <Typography
              sx={{
                fontFamily: "Roboto, sans-serif",
                fontSize: "13px",
                fontWeight: 600,
                color: "#334155",
              }}
            >
              {seriesLabel}
            </Typography>
          </Stack>
          <Typography
            sx={{
              fontFamily: "Roboto, sans-serif",
              fontWeight: 800,
              fontSize: "28px",
              lineHeight: 1.05,
              color: accent,
              fontVariantNumeric: "tabular-nums",
              letterSpacing: "-0.03em",
            }}
          >
            {valueStr}
          </Typography>
        </Stack>
      </Box>
    </Paper>
  );
}

function LegendDot({ color }) {
  return (
    <Box
      sx={{
        width: 8,
        height: 8,
        borderRadius: "50%",
        backgroundColor: color,
        flexShrink: 0,
      }}
    />
  );
}

function AlertGraphPanel({ aggregateData, trendData }) {
  const useTrend = Array.isArray(trendData) && trendData.length > 0;
  const showCriticalSeries =
    useTrend &&
    trendData.some((row) => Number(row?.critical) > 0);

  return (
    <Box sx={{ width: "100%", minHeight: "500px" }}>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          minHeight: "40px",
          gap: 2,
          mb: 2,
        }}
      >
        <Typography
          sx={{
            fontFamily: "Roboto, sans-serif",
            fontWeight: 600,
            fontSize: "16px",
            color: "#212121",
          }}
        >
          {useTrend ? "Alert trend by day" : "Alert graph (totals)"}
        </Typography>
        <Stack
          direction="row"
          alignItems="center"
          spacing={2}
          flexWrap="wrap"
          useFlexGap
          sx={{ gap: 2 }}
        >
          {useTrend ? (
            <>
              {[
                { label: "AI", color: TREND_COLOR_AI },
                { label: "IoT", color: TREND_COLOR_IOT },
                { label: "System", color: TREND_COLOR_SYSTEM },
                ...(showCriticalSeries
                  ? [{ label: "Critical", color: TREND_COLOR_CRITICAL }]
                  : []),
              ].map(({ label, color }) => (
                <Stack
                  key={label}
                  direction="row"
                  alignItems="center"
                  spacing={0.75}
                >
                  <LegendDot color={color} />
                  <Typography
                    sx={{
                      fontFamily: "Roboto, sans-serif",
                      fontSize: "12px",
                      color: "#616161",
                      fontWeight: 500,
                    }}
                  >
                    {label}
                  </Typography>
                </Stack>
              ))}
            </>
          ) : (
            <>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography
                  sx={{
                    fontFamily: "Roboto, sans-serif",
                    fontSize: "12px",
                    color: "#616161",
                    fontWeight: 500,
                  }}
                >
                  Total
                </Typography>
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  <LegendDot color="#2932E5" />
                  <LegendDot color="#FF9800" />
                  <LegendDot color="#00C853" />
                </Stack>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={0.75}>
                <LegendDot color={CRITICAL_BAR} />
                <Typography
                  sx={{
                    fontFamily: "Roboto, sans-serif",
                    fontSize: "12px",
                    color: "#616161",
                    fontWeight: 500,
                  }}
                >
                  Critical
                </Typography>
              </Stack>
            </>
          )}
        </Stack>
      </Box>

      <Box
        sx={{
          width: "100%",
          height: 450,
          backgroundColor: "#FAFAFA",
          borderRadius: "8px",
          border: "1px solid #EEEEEE",
          pt: 1,
          overflow: "visible",
        }}
      >
        <ResponsiveContainer width="100%" height="100%">
          {useTrend ? (
            <BarChart
              data={trendData}
              margin={{ top: 36, right: 16, left: 8, bottom: 40 }}
              barCategoryGap="18%"
              barGap={2}
            >
              <XAxis
                dataKey="name"
                interval={0}
                tick={{
                  fontFamily: "Roboto, sans-serif",
                  fontSize: 10,
                  fill: "#616161",
                }}
                axisLine={{ stroke: "#BDBDBD" }}
                tickLine={false}
              />
              <YAxis
                allowDecimals={false}
                width={44}
                tick={{ fontSize: 11, fill: "#616161" }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                shared={false}
                offset={10}
                allowEscapeViewBox={{ x: false, y: false }}
                cursor={{ fill: "rgba(41, 50, 229, 0.06)" }}
                wrapperStyle={{ outline: "none", zIndex: 5 }}
                content={(props) => <TrendChartTooltip {...props} />}
              />
              <Customized component={TrendChartDayTotalLabels} />
              <Bar
                dataKey="stackAiNc"
                stackId="trend"
                fill={TREND_COLOR_AI}
                name="AI"
                radius={
                  showCriticalSeries ? [0, 0, 0, 0] : [4, 4, 0, 0]
                }
              >
                <LabelList content={<TrendSegmentLabelAi />} />
              </Bar>
              <Bar
                dataKey="stackIotNc"
                stackId="trend"
                fill={TREND_COLOR_IOT}
                name="IoT"
                radius={[0, 0, 0, 0]}
              >
                <LabelList content={<TrendSegmentLabelIot />} />
              </Bar>
              <Bar
                dataKey="stackSystemNc"
                stackId="trend"
                fill={TREND_COLOR_SYSTEM}
                name="System"
                radius={[0, 0, 0, 0]}
              >
                <LabelList content={<TrendSegmentLabelSystem />} />
              </Bar>
              {showCriticalSeries ? (
                <Bar
                  dataKey="stackCritical"
                  stackId="trend"
                  fill={TREND_COLOR_CRITICAL}
                  name="Critical"
                  radius={[4, 4, 0, 0]}
                >
                  <LabelList content={<TrendSegmentLabelCritical />} />
                </Bar>
              ) : null}
            </BarChart>
          ) : (
            <BarChart
              data={aggregateData}
              margin={{ top: 16, right: 24, left: 8, bottom: 16 }}
              barCategoryGap="32%"
              barGap={6}
            >
              <XAxis
                dataKey="name"
                tick={{
                  fontFamily: "Roboto, sans-serif",
                  fontSize: 12,
                  fill: "#616161",
                }}
                axisLine={{ stroke: "#BDBDBD" }}
                tickLine={false}
              />
              <YAxis hide width={0} />
              <Bar dataKey="total" radius={[0, 0, 0, 0]}>
                {aggregateData.map((entry) => (
                  <Cell key={`total-${entry.name}`} fill={entry.totalFill} />
                ))}
              </Bar>
              <Bar
                dataKey="critical"
                fill={CRITICAL_BAR}
                radius={[0, 0, 0, 0]}
              />
            </BarChart>
          )}
        </ResponsiveContainer>
      </Box>
    </Box>
  );
}
